import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Checkbox, Text, Flex, HStack, VStack, IconButton, Image,
  Spinner, Center, Badge, Tooltip, useDisclosure, Menu, MenuButton, MenuList, MenuItem, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Input, Button, Slider, SliderTrack, SliderFilledTrack, SliderThumb,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiMail, FiFileText, FiCalendar, FiMenu,
  FiShoppingCart, FiCheck, FiEdit2, FiClock, FiTruck, FiPlay, FiBookOpen, FiCopy, FiFolder, FiUserCheck, FiMessageSquare, FiAlertCircle, FiClipboard, FiKey, FiUploadCloud } from 'react-icons/fi';
import ActionTypeSelect from './ActionTypeSelect';
import DocumentsModal from './DocumentsModal';
import TaskCloneModal from './TaskCloneModal';
import TaskCloneHistoryModal from './TaskCloneHistoryModal';
import AssignUserModal from './AssignUserModal';
import AddContactModal from './AddContactModal';
import ProjectSearch from './ProjectSearch';
import WorkCentreSelect from './WorkCentreSelect';
import InlineEdit from './InlineEdit';
import DeadlineCell from './DeadlineCell';
import CommunicationsModal from './CommunicationsModal';
import TaskProcSafeModal from './TaskProcSafeModal';
import ConfirmDialog from './ConfirmDialog';
import NotesModal from './NotesModal';
import ConfirmModal from './ConfirmModal';
import InvoiceModal from './InvoiceModal';
import CalendarModal from './CalendarModal';
import ResourceBookingModal from './ResourceBookingModal';
import PlannedVisitsTaskModal from './PlannedVisitsTaskModal';
import StatusModal from './StatusModal';
import TaskEditModal from './TaskEditModal';
import SuppliersModal from './SuppliersModal';
import MileageModal from './MileageModal';
import CompanyModal from './CompanyModal';
import ContactModal from './ContactModal';
import CompanyQuickViewModal from './CompanyQuickViewModal';
import ContactQuickViewModal from './ContactQuickViewModal';
import ContactsListModal from './ContactsListModal';
import CompaniesListModal from './CompaniesListModal';
import useMenuPosition from '../hooks/useMenuPosition';
import ProjectDetailModal from './ProjectDetailModal';
import WorkSessionModal from './WorkSessionModal';
import { AppointmentModal } from './BookMyDiaryDashboard';
import FeedbackModal from './FeedbackModal';
import IncidentBlogPage from './IncidentBlogPage';
import DashboardColumnsModal, { getColumnConfig, isColVisible, colWidth, COLUMN_DEFS } from './DashboardColumnsModal';
import HoldModal from './HoldModal';
import ProspectsModal from './ProspectsModal';
import GanttModal from './GanttModal';
import ProgressModal from './ProgressModal';
import TemplatePickerModal from './TemplatePickerModal';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

export default function MainGrid({
  projects, rowGroups, allRowGroups, loading, onUpdateProject, onDeleteProject, onAddProject, onRefresh, taskUnread = {}, highlightTaskId = null,
  onOpenBookkeeper, onFocusProject, autoOpenComms, onOpenInvoiceIntake
}) {
  const { user: authUser } = useAuth();
  const rowColor1 = authUser?.row_color_1 || '#FFFFFF';
  const rowColor2 = authUser?.row_color_2 || '#F7FAFC';
  const highlightColor = authUser?.highlight_color || '#EE7203';
  const showLogos = authUser?.show_logos !== false;
  const showAvatars = authUser?.show_avatars !== false;
  const logoSize = authUser?.logo_size || 20;
  const dimColor = authUser?.dim_empty_icons_color || '#CBD5E0';
  const cc = getColumnConfig(authUser?.dashboard_config);
  const v = (key) => isColVisible(cc, key);
  const cw = (key) => `${colWidth(cc, key)}px`;
  const columnsDisclosure = useDisclosure();
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedSupplierGridTasks, setSelectedSupplierGridTasks] = useState([]);
  const [selectedCalGridTasks, setSelectedCalGridTasks] = useState([]);
  const [statusProjectId, setStatusProjectId] = useState(null);
  const [prospectEditData, setProspectEditData] = useState(null);
  const prospectDisclosure = useDisclosure();
  const ganttDisclosure = useDisclosure();
  const [ganttProjectId, setGanttProjectId] = useState(null);
  const progressDisclosure = useDisclosure();
  const [progressTaskId, setProgressTaskId] = useState(null);
  const [progressTaskTitle, setProgressTaskTitle] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteFinalConfirm, setDeleteFinalConfirm] = useState(null); // second confirm step
  const [statusGroupIds, setStatusGroupIds] = useState([]);
  const [taskProject, setTaskProject] = useState(null);

  // Auto-open comms modal when triggered from inbox navigation
  useEffect(() => {
    if (!autoOpenComms || !autoOpenComms.taskId || !autoOpenComms.projectId) return;
    // Small delay to let the grid render/scroll settle
    const timer = setTimeout(() => {
      openComms(autoOpenComms.projectId, autoOpenComms.taskId);
    }, 400);
    return () => clearTimeout(timer);
  }, [autoOpenComms]); // eslint-disable-line
  const commDisclosure = useDisclosure();
  const docsDisclosure = useDisclosure();
  const taskCloneDisclosure = useDisclosure();
  const taskCloneHistoryDisclosure = useDisclosure();
  const notesDisclosure = useDisclosure();
  const invoiceDisclosure = useDisclosure();
  const invoiceInDisclosure = useDisclosure();
  const calendarDisclosure = useDisclosure();
  const statusDisclosure = useDisclosure();
  const taskDisclosure = useDisclosure();
  const suppliersDisclosure = useDisclosure();
  const mileageDisclosure = useDisclosure();
  const feedbackDisclosure = useDisclosure();
  const incidentDisclosure = useDisclosure();
  const holdDisclosure = useDisclosure();
  const diaryDisclosure = useDisclosure();
  const [diaryPrefill, setDiaryPrefill] = useState(null);
  const [diaryContactPicker, setDiaryContactPicker] = useState(null);
  // Todo list modals
  const [todoDeadlineTarget, setTodoDeadlineTarget] = useState(null); // {id, type:'task'|'subtask', title}
  const [todoDeadlineForm, setTodoDeadlineForm] = useState({ date:'', time:'', deadlineType:'hard', leeway:1 });
  const [todoUsersTarget, setTodoUsersTarget] = useState(null); // {id, type, title}
  const [todoUsersAll, setTodoUsersAll] = useState([]);
  const [todoUsersSelected, setTodoUsersSelected] = useState([]);
  const [savingTodoUsers, setSavingTodoUsers] = useState(false); // { contacts, project, task }
  const [holdTask, setHoldTask] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // { title, message, onConfirm }

  const [contextMenu, setContextMenu] = useState(null);
  const [entityMenu, setEntityMenu] = useState(null); // {x,y,type:'company'|'contact',entity,projectId,taskId}
  const [addEntityModal, setAddEntityModal] = useState(null); // {type:'company'|'contact',projectId,taskId}
  const [entitySearch, setEntitySearch] = useState('');
  const [entityResults, setEntityResults] = useState([]);
  const [showCreateEntity, setShowCreateEntity] = useState(false);
  const [quickViewCompany, setQuickViewCompany] = useState(null); // company id
  const [quickViewContact, setQuickViewContact] = useState(null); // contact id
  // Full-edit contact modal — opened from the pencil icon on the quick view.
  // We launch ContactsListModal with initialContactId, which auto-opens that
  // contact's full editor (the same one used from the Contacts power dashboard).
  const [editContactId, setEditContactId] = useState(null);
  // Company-edit launcher state — pencil icon on CompanyQuickViewModal sets
  // this, and the mounted CompaniesListModal below jumps straight into the
  // edit form for that company. Mirrors the contact pattern above.
  const [editCompanyId, setEditCompanyId] = useState(null);
  const [hideHeld, setHideHeld] = useState(false);
  const [showTotals, setShowTotals] = useState(false);
  const [showTotalsModal, setShowTotalsModal] = useState(false);
  const [hideNoInvoice, setHideNoInvoice] = useState(true);
  const [hideNoFinancialGroups, setHideNoFinancialGroups] = useState(true);
  const [countOnlyPrimary, setCountOnlyPrimary] = useState(true);
  const [invDateField, setInvDateField] = useState('issue'); // 'issue' | 'due'
  const [invCurrencies, setInvCurrencies] = useState(['gbp']); // currencies to include in cash totals
  const [invIncludeBarterVat, setInvIncludeBarterVat] = useState(false); // include BBX VAT liability in VAT/BAL columns
  const [invDateFrom, setInvDateFrom] = useState('');
  const [invDateTo, setInvDateTo] = useState('');
  const [filterInStatuses, setFilterInStatuses]   = useState([]); // [] = show all
  const [filterOutStatuses, setFilterOutStatuses] = useState([]);
  const [hideDone, setHideDone] = useState(true);
  const [priorityProject, setPriorityProject] = useState(null);
  const [detailProjectId, setDetailProjectId] = useState(null);
  const detailDisclosure = useDisclosure();
  const [workSessionTask, setWorkSessionTask] = useState(null);
  const [cloneTask, setCloneTask] = useState(null);
  const [assignUserTarget, setAssignUserTarget] = useState(null); // {type,id,name}
  const assignUserDisclosure = useDisclosure();
  const [addContactTarget, setAddContactTarget] = useState(null); // {type,id,name}
  const addContactDisclosure = useDisclosure();
  const [templatePickerGroup, setTemplatePickerGroup] = useState(null);
  const templatePickerDisclosure = useDisclosure();
  const workSessionDisclosure = useDisclosure();
  const [taskMenu, setTaskMenu] = useState(null);
  const toast = useToast();
  // Whether the right-clicked task currently has a todo_items row linking
  // it for this user (the new TODO list model). Resolved async after the
  // menu opens; the menu label uses it to show "Remove" vs "Add".
  // `null` = still loading, `true|false` = known state.
  const [taskMenuLinkedTodo, setTaskMenuLinkedTodo] = useState(null);
  useEffect(() => {
    if (!taskMenu?.task?.id) { setTaskMenuLinkedTodo(null); return; }
    let cancelled = false;
    setTaskMenuLinkedTodo(null);
    api.get(`/todo-items/linked-status?task_id=${taskMenu.task.id}`)
      .then(r => { if (!cancelled) setTaskMenuLinkedTodo(!!r.data?.linked); })
      .catch(() => { if (!cancelled) setTaskMenuLinkedTodo(false); });
    return () => { cancelled = true; };
  }, [taskMenu?.task?.id]);

  // ── Context-menu refs + viewport-clamp hooks ─────────────────────────────
  // Right-clicks near the bottom or right edge of the viewport used to push
  // the menu off-screen because we positioned at raw clientX/clientY with no
  // bounds. Each menu now measures itself after mounting and the hook
  // returns a clamped {left, top} that keeps the whole list visible.
  const projectMenuRef = useRef(null);
  const taskMenuRef    = useRef(null);
  const shownHiddenProjectRef = useRef(null); // project with hidden tasks revealed
  // Branded delete-task confirmation (no browser confirm) with the
  // tenant-configured visibility window quoted in the message.
  const [deleteTaskTarget, setDeleteTaskTarget] = useState(null);
  const [deletingTask, setDeletingTask] = useState(false);
  const [configDeleteDays, setConfigDeleteDays] = useState(7);
  useEffect(() => {
    api.get('/settings').then(({ data }) => {
      const v = parseInt(data?.show_completed_deleted_days);
      if (!isNaN(v)) setConfigDeleteDays(v);
    }).catch(() => {});
  }, []);
  const entityMenuRef  = useRef(null);
  const projectMenuPos = useMenuPosition(projectMenuRef, contextMenu);
  const taskMenuPos    = useMenuPosition(taskMenuRef,    taskMenu);
  const entityMenuPos  = useMenuPosition(entityMenuRef,  entityMenu);

  // Drag reorder state
  const [dragProjectId, setDragProjectId] = useState(null);
  const [dragGroupId, setDragGroupId] = useState(null);
  const [dragOverProjectId, setDragOverProjectId] = useState(null);

  const handleDragStart = (e, projectId, groupId) => {
    setDragProjectId(projectId);
    setDragGroupId(groupId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', projectId);
    // Make the drag image slightly transparent
    if (e.currentTarget.closest('tr')) {
      e.currentTarget.closest('tr').style.opacity = '0.4';
    }
  };

  const handleDragEnd = (e) => {
    if (e.currentTarget.closest('tr')) {
      e.currentTarget.closest('tr').style.opacity = '1';
    }
    setDragProjectId(null);
    setDragGroupId(null);
    setDragOverProjectId(null);
  };

  const handleDragOver = (e, projectId, groupId) => {
    e.preventDefault();
    // Only allow drop within same group
    if (groupId !== dragGroupId) return;
    e.dataTransfer.dropEffect = 'move';
    setDragOverProjectId(projectId);
  };

  const handleDrop = async (e, targetProjectId, groupId) => {
    e.preventDefault();
    setDragOverProjectId(null);
    if (!dragProjectId || dragProjectId === targetProjectId || groupId !== dragGroupId) return;

    // Get current group projects in order
    const groupProjects = grouped[groupId] || [];
    const projectIds = groupProjects.map(p => p.id);

    // Remove dragged project and insert before target
    const fromIdx = projectIds.indexOf(dragProjectId);
    const toIdx = projectIds.indexOf(targetProjectId);
    if (fromIdx === -1 || toIdx === -1) return;

    projectIds.splice(fromIdx, 1);
    projectIds.splice(toIdx, 0, dragProjectId);

    // Save new order
    try {
      await api.put('/projects/reorder', { project_ids: projectIds });
      onRefresh();
    } catch(e) { console.error('Reorder failed:', e); }

    setDragProjectId(null);
    setDragGroupId(null);
  };

  const openComms = (pid, tid) => { setSelectedProjectId(pid); setSelectedTaskId(tid); commDisclosure.onOpen(); };
  // Procedures & Password Safe panel — opened from the clipboard/key icons.
  const [procSafeTarget, setProcSafeTarget] = useState(null); // { taskId, tab }
  const openProcSafe = (tid, tab) => setProcSafeTarget({ taskId: tid, tab });
  const openDocs = (pid, tid) => { setSelectedProjectId(pid); setSelectedTaskId(tid); docsDisclosure.onOpen(); };
  const openNotes = (pid, tid) => { setSelectedProjectId(pid); setSelectedTaskId(tid); notesDisclosure.onOpen(); };
  const openInvoice = (pid, tid) => { setSelectedProjectId(pid); setSelectedTaskId(tid); invoiceDisclosure.onOpen(); };
  // Supplier (received) invoices: the Upload Invoices page is the interface
  // for recording them against a task; the classic modal remains reachable
  // from that page for invoices recorded the old way.
  const openInvoiceIn = (pid, tid) => {
    if (onOpenInvoiceIntake) { onOpenInvoiceIntake(pid, tid); return; }
    setSelectedProjectId(pid); setSelectedTaskId(tid); invoiceInDisclosure.onOpen();
  };
  const openCalendar = (pid, tid, gridTasks) => { setSelectedProjectId(pid); setSelectedTaskId(tid); setSelectedCalGridTasks(Array.isArray(gridTasks) ? gridTasks : []); calendarDisclosure.onOpen(); };
  const openDiaryForTask = async (project, task) => {
    // Fetch contacts and users assigned specifically to this task/project — NOT all contacts
    const [projectContactsRes, taskContactsRes, projectUsersRes, taskUsersRes] = await Promise.allSettled([
      api.get(`/projects/${project.id}/contacts`),
      api.get(`/tasks/${task.id}/contacts`),
      api.get(`/projects/${project.id}/users`),
      api.get(`/tasks/${task.id}/users`),
    ]);

    // Merge contacts (deduplicated by id)
    const allContacts = [
      ...(projectContactsRes.status === 'fulfilled' ? projectContactsRes.value.data || [] : []),
      ...(taskContactsRes.status === 'fulfilled' ? taskContactsRes.value.data || [] : []),
    ];
    const seenC = new Set();
    const contacts = allContacts.filter(c => { if (seenC.has(c.id)) return false; seenC.add(c.id); return true; });

    // Merge users (deduplicated)
    const allUsers = [
      ...(projectUsersRes.status === 'fulfilled' ? projectUsersRes.value.data || [] : []),
      ...(taskUsersRes.status === 'fulfilled' ? taskUsersRes.value.data || [] : []),
    ];
    const seenU = new Set();
    const taskUsers = allUsers.filter(u => { const k = u.id || u.user_id; if (seenU.has(k)) return false; seenU.add(k); return true; });

    const prefillBase = { project_id: String(project.id), task_id: String(task.id), company_id: project.company_id || null };

    if (contacts.length > 1 || taskUsers.length > 0) {
      setDiaryContactPicker({ contacts, users: taskUsers, project, task });
    } else {
      setDiaryPrefill({ ...prefillBase, contact_ids: contacts.length === 1 ? [contacts[0].id] : [], prefill_contacts: contacts.length === 1 ? [contacts[0]] : [] });
      diaryDisclosure.onOpen();
    }
  };
  const bookingsDisclosure = useDisclosure();
  const openBookings = (pid, tid) => { setSelectedProjectId(pid); setSelectedTaskId(tid); bookingsDisclosure.onOpen(); };
  const plannedVisitsDisclosure = useDisclosure();
  const [plannedVisitsTaskId, setPlannedVisitsTaskId] = React.useState(null);
  const [plannedVisitsTaskTitle, setPlannedVisitsTaskTitle] = React.useState('');
  const [plannedVisitsProjectId, setPlannedVisitsProjectId] = React.useState(null);
  const openPlannedVisits = (tid, title, pid) => { setPlannedVisitsTaskId(tid); setPlannedVisitsTaskTitle(title || ''); setPlannedVisitsProjectId(pid || null); plannedVisitsDisclosure.onOpen(); };
  const openSuppliers = (pid, tid, gridTasks) => { setSelectedProjectId(pid); setSelectedTaskId(tid); setSelectedSupplierGridTasks(Array.isArray(gridTasks) ? gridTasks : []); suppliersDisclosure.onOpen(); };
  const openMileage = (pid, tid) => { setSelectedProjectId(pid); setSelectedTaskId(tid); mileageDisclosure.onOpen(); };
  const openFeedback = (pid, tid) => { setSelectedProjectId(pid); setSelectedTaskId(tid); feedbackDisclosure.onOpen(); };
  const openIncident = (pid, tid) => { setSelectedProjectId(pid); setSelectedTaskId(tid); incidentDisclosure.onOpen(); };
  const openTask = (project, task) => { setTaskProject({ ...project, _task: task }); taskDisclosure.onOpen(); };

  const handleTaskSave = async (data) => {
    if (!taskProject?._task) return;
    const { task_title, job_description, ...rest } = data;
    await api.put(`/tasks/${taskProject._task.id}`, { task_title, description: job_description, ...rest });
    // Don't refresh here — let the modal onClose handler do it
  };

  const handleCloneProject = async (projectId) => {
    try {
      await api.post(`/projects/${projectId}/clone`, {});
      if (onRefresh) onRefresh();
    } catch(e) {}
  };

  const handleRestoreProject = async (projectId) => {
    try {
      await api.post(`/projects/${projectId}/restore`);
      if (onRefresh) onRefresh();
    } catch(e) {}
  };

  const handleAddTask = async (projectId) => {
    try {
      await api.post('/tasks', { project_id: projectId, task_title: 'New Task' });
      onRefresh();
    } catch(e) { console.error(e); }
  };

  const handleTaskUpdate = async (taskId, fields) => {
    try {
      await api.put(`/tasks/${taskId}`, fields);
      // No full refresh — inline editors manage their own state
      // Only structural changes (add/delete/move) need full refresh
    } catch(e) { console.error(e); }
  };

  const handleTaskDelete = async (taskId) => {
    setConfirmAction({
      title: 'Delete Task',
      message: 'Are you sure you want to delete this task?',
      onConfirm: async () => {
        try { await api.delete(`/tasks/${taskId}`); onRefresh(); } catch(e) { console.error(e); }
      }
    });
  };

  const handleMove = async (projectId, fromGroupId, toGroupId) => {
    try { await api.put(`/projects/${projectId}/move`, { from_group_id: fromGroupId, to_group_id: toGroupId }); onRefresh(); } catch(e) {}
  };

  const openStatus = (project) => {
    setStatusProjectId(project.id);
    setStatusGroupIds(project.row_groups ? project.row_groups.map(g => g.row_group_id) : []);
    statusDisclosure.onOpen();
  };

  useEffect(() => { const h = () => { setContextMenu(null); setEntityMenu(null); setTaskMenu(null); }; document.addEventListener('click', h); return () => document.removeEventListener('click', h); }, []);
  const handleContextMenu = (e, project) => { e.preventDefault(); setContextMenu({ x: e.clientX, y: e.clientY, project }); };

  const handleEntityContext = (e, type, entity, projectId, taskId) => {
    e.preventDefault(); e.stopPropagation();
    setEntityMenu({ x: e.clientX, y: e.clientY, type, entity, projectId, taskId: taskId || null });
  };

  const handleEntityRemove = async () => {
    if (!entityMenu) return;
    const { type, entity, projectId, taskId } = entityMenu;
    try {
      if (taskId) {
        // Tasks use DELETE endpoint for companies; contacts use PUT with updated array
        if (type === 'company') {
          await api.delete(`/tasks/${taskId}/companies/${entity.id}`);
        } else {
          // Fetch current task contacts, filter out the removed one, PUT back
          const { data: current } = await api.get(`/tasks/${taskId}/contacts`).catch(() => ({ data: [] }));
          const remaining = (current || [])
            .map(c => c.id || c.contact_id)
            .filter(id => id !== entity.id);
          await api.put(`/tasks/${taskId}/contacts`, { contact_ids: remaining });
        }
      } else {
        if (type === 'company') {
          // Projects companies use DELETE endpoint
          await api.delete(`/projects/${projectId}/companies/${entity.company_id || entity.id}`);
        } else {
          // Projects contacts use PUT with updated array — no DELETE endpoint exists
          const proj = projects.find(p => p.id === projectId);
          const currentContacts = proj?.contacts || [];
          const remaining = currentContacts
            .map(c => c.id || c.contact_id)
            .filter(id => id !== entity.id);
          await api.put(`/projects/${projectId}/contacts`, { contact_ids: remaining });
        }
      }
      onRefresh();
    } catch(e) { console.error(e); }
    setEntityMenu(null);
  };

  const openAddEntity = (type, projectId, taskId) => {
    const proj = projects.find(p => p.id === projectId);
    const companies = proj?.companies || [];
    setAddEntityModal({ type, projectId, taskId: taskId || null, companies });
    setEntitySearch(''); setEntityResults([]);
    setEntityMenu(null);
    // Load initial list immediately
    setTimeout(() => searchEntities('', type), 50);
  };

  const searchEntities = useCallback(async (q, type) => {
    const entityType = type || addEntityModal?.type;
    if (!entityType) return;
    try {
      const endpoint = entityType === 'company'
        ? `/companies/search?q=${encodeURIComponent(q)}&limit=30`
        : `/contacts/search?q=${encodeURIComponent(q)}&limit=30`;
      const { data } = await api.get(endpoint);
      setEntityResults(data);
    } catch(e) {}
  }, [addEntityModal?.type]);

  const handleEntityAdd = async (entity) => {
    if (!addEntityModal) return;
    const { type, projectId, taskId } = addEntityModal;
    try {
      if (taskId) {
        const key = type === 'company' ? 'company_id' : 'contact_id';
        await api.post(`/tasks/${taskId}/${type === 'company' ? 'companies' : 'contacts'}`, { [key]: entity.id });
      } else {
        if (type === 'company') {
          await api.post(`/projects/${projectId}/companies`, { company_id: entity.id, is_primary: false });
        } else {
          const proj = projects.find(p => p.id === projectId);
          const existingIds = (proj?.contacts || []).map(c => c.id);
          await api.put(`/projects/${projectId}/contacts`, { contact_ids: [...existingIds, entity.id] });
        }
      }
      onRefresh();
    } catch(e) { console.error(e); }
    setAddEntityModal(null); setEntitySearch(''); setEntityResults([]);
  };

  if (loading) return <Center py={20}><Spinner size="lg" color="brand.500" /></Center>;

  // Group projects by row_group
  const grouped = {};
  rowGroups.forEach(g => { grouped[g.id] = []; });
  projects.forEach(p => {
    const groups = p.row_groups?.length > 0 ? p.row_groups : (p.row_group_id ? [{ row_group_id: p.row_group_id }] : []);
    groups.forEach(g => { if (grouped[g.row_group_id]) grouped[g.row_group_id].push({ ...p, _display_group_id: g.row_group_id }); });
  });

  const COLS = COLUMN_DEFS.filter(d => v(d.key)).length;

  // Hover styles: highlight on data cells only, icon cells stay normal
  // EXCLUDES .dropdown-popup and its children
  const dataHover = {
    '&:hover .data-cell': {
      backgroundColor: `${highlightColor} !important`, color: 'white !important',
    },
    '&:hover .data-cell > p, &:hover .data-cell > span, &:hover .data-cell > div:not(.dropdown-popup)': {
      color: 'white !important',
    },
    '&:hover .data-cell svg': { color: 'white !important', stroke: 'white !important' },
    '&:hover .data-cell > .chakra-badge, &:hover .data-cell .chakra-badge': {
      color: 'white !important',
      backgroundColor: 'transparent !important',
      background: 'transparent !important',
      boxShadow: 'inset 0 0 0 1px white !important',
    },
    '&:hover .data-cell .chakra-checkbox__control': {
      borderColor: 'white !important',
      backgroundColor: 'transparent !important',
    },
    // Dropdown popups: ALWAYS black on white
    '&:hover .data-cell .dropdown-popup, &:hover .data-cell .dropdown-popup *': {
      color: 'black !important',
      backgroundColor: 'white !important',
    },
    '&:hover .data-cell .dropdown-popup li:hover, &:hover .data-cell .dropdown-popup li:hover *': {
      backgroundColor: '#EBF8FF !important',
    },
    // Chakra menu (for move/context): black on white
    '& .chakra-menu__menu-list, & .chakra-menu__menu-list *': {
      color: 'black !important',
    },
    // Inputs: white when not focused, black on white when focused
    '&:hover .data-cell input:not(:focus), &:hover .data-cell select:not(:focus)': {
      color: 'white !important', backgroundColor: 'transparent !important',
    },
    '&:hover .data-cell input:not(:focus)::placeholder': {
      color: 'white !important', opacity: '0.8 !important',
    },
    '&:hover .data-cell input:not(:focus)::-webkit-input-placeholder': {
      color: 'white !important', opacity: '0.8 !important',
    },
    '&:hover .data-cell input:not(:focus)::-moz-placeholder': {
      color: 'white !important', opacity: '0.8 !important',
    },
    '&:hover .data-cell input:focus, &:hover .data-cell select:focus': {
      color: 'black !important', backgroundColor: 'white !important',
      border: '1px solid #CBD5E0 !important', borderRadius: '4px !important',
    },
  };

  const renderTaskRow = (project, task, isFirst, group, rowIndex) => {
    if (hideHeld && task.is_held) return null;
    if (hideDone && (project.is_done || task.is_complete)) return null;
    if (showTotals && hideNoInvoice) {
      const hasInvoice = parseFloat(task.inv_in_total || 0) > 0 || parseFloat(task.inv_out_total || 0) > 0
        || (task.inv_in_count || 0) > 0 || (task.inv_out_count || 0) > 0;
      if (!hasInvoice) return null;
    }
    if (showTotals && (invDateFrom || invDateTo) && !taskPassesDateFilter(task)) return null;

    const altBg = rowIndex % 2 === 0 ? rowColor1 : rowColor2;

    return (
      <Tr
        key={`task-${task.id}-${group.id}`}
        data-project-id={project.id}
        bg={project.deleted_at ? 'red.50' : task.is_complete ? 'green.50' : task.is_held ? 'red.50' :
          dragOverProjectId === project.id && isFirst ? 'blue.50' : altBg}
        opacity={project.deleted_at ? 0.6 : task.is_complete ? 0.6 : dragProjectId === project.id ? 0.4 : 1}
        sx={task.is_complete ? {} : dataHover}
        onDragOver={isFirst ? (e) => handleDragOver(e, project.id, group.id) : undefined}
        onDrop={isFirst ? (e) => handleDrop(e, project.id, group.id) : undefined}
        borderTop={dragOverProjectId === project.id && isFirst ? '2px solid' : undefined}
        borderTopColor={dragOverProjectId === project.id && isFirst ? 'blue.400' : undefined}
      >
        {/* Project-level cells — only on first task */}
        {isFirst ? (
          <>
            {v('drag') && <Td className="data-cell" px={0} textAlign="center">
              <Flex direction="column" align="center" gap={0}>
                <Box cursor="grab" draggable onDragStart={(e) => handleDragStart(e, project.id, group.id)}
                  onDragEnd={handleDragEnd} color="gray.300" _hover={{ color: 'gray.600' }} mb={-1}>
                  <FiMenu size={12} />
                </Box>
                <Menu isLazy>
                  <MenuButton as={Box} cursor="pointer" color="gray.300" _hover={{ color: 'gray.600' }}
                    fontSize="10px" lineHeight="1" mt={-0.5}>▾</MenuButton>
                  <MenuList fontSize="12px" maxH="300px" overflowY="auto" zIndex={200}>
                    <MenuItem icon={<FiCopy />} onClick={() => handleCloneProject(project.id)}>Clone Project</MenuItem>
                    {(allRowGroups || rowGroups).filter(g => g.id !== project._display_group_id).map(g => (
                      <MenuItem key={g.id} onClick={() => handleMove(project.id, project._display_group_id, g.id)}>Move to {g.name}</MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </Flex>
            </Td>}
            {v('done') && <Td className="data-cell" textAlign="center">
              <Checkbox size="sm" isChecked={!!project.is_done} colorScheme="green"
                onChange={(e) => onUpdateProject(project.id, { is_done: e.target.checked })} />
            </Td>}
            {v('wc') && <Td className="data-cell" textAlign="center" px={0}>
              <WorkCentreSelect value={project.work_centre_name || ''} workCentreId={project.work_centre_id}
                onSelect={(id) => onUpdateProject(project.id, { work_centre_id: id })} />
            </Td>}
            {v('project') && <Td className="data-cell" onContextMenu={(e) => handleContextMenu(e, project)}>
              {project.project_logo_url ? (
                <Tooltip label={project.project_name || 'Project'} fontSize="xs" hasArrow placement="right">
                  <Box cursor="pointer" px={1} py={0.5} display="inline-flex" alignItems="center"
                    onClick={() => { setDetailProjectId(project.id); detailDisclosure.onOpen(); }}>
                    <Image src={`/api/files/${project.project_logo_url}`} alt={project.project_name || 'Project'}
                      h="22px" maxW="100px" objectFit="contain" />
                  </Box>
                </Tooltip>
              ) : (
                <ProjectSearch value={project.project_name || ''} onSave={(val) => onUpdateProject(project.id, { project_name: val })} />
              )}
              {project.deleted_at && <Badge fontSize="7px" colorScheme="red" ml={1}>DELETED</Badge>}
              {!!project.is_template && <Badge fontSize="7px" colorScheme="purple" ml={1}>TEMPLATE</Badge>}
              {project.row_groups?.length > 1 && (
                <Flex mt={0.5} gap={0.5} flexWrap="wrap">
                  {project.row_groups.filter(g => g.row_group_id !== project._display_group_id).map(g => (
                    <Badge key={g.row_group_id} fontSize="7px" colorScheme="gray" variant="outline">{g.row_group_name}</Badge>
                  ))}
                </Flex>
              )}
            </Td>}
            {v('project_date') && <Td className="data-cell">
              <InlineEdit value={project.commissioning_date ? project.commissioning_date.split('T')[0] : ''}
                type="date" onSave={(val) => onUpdateProject(project.id, { commissioning_date: val || null })} />
            </Td>}
            {v('deadline') && <Td className="data-cell">
              <DeadlineCell projectId={project.id} deadlineDate={project.deadline_date}
                deadlineType={project.deadline_type} onRefresh={onRefresh} />
            </Td>}
            {v('client') && <Td className="data-cell" onContextMenu={(e) => {
              const cos = project.companies || [];
              if (cos.length === 1) handleEntityContext(e, 'company', cos[0], project.id);
              else if (cos.length === 0) { e.preventDefault(); openAddEntity('company', project.id); }
            }}>
              {(project.companies || []).length === 0 ? (
                <Text fontSize="11px" color="gray.400" cursor="pointer" onClick={() => openAddEntity('company', project.id)}>+ Company</Text>
              ) : (
                <Flex gap={0.5} flexWrap="wrap" px={1} py={0.5} align="center">
                  {(project.companies || []).map(c => (
                    showLogos && c.logo_url ? (
                      <Tooltip key={c.id} label={c.name} fontSize="xs">
                        <Image src={`/api/files/${c.logo_url}`} alt={c.name} h={`${logoSize}px`} maxW={`${Math.round(logoSize * 2.5)}px`} objectFit="contain"
                          cursor="pointer"
                          onClick={(e) => { e.stopPropagation(); setQuickViewCompany(c.id); }}
                          onContextMenu={(e) => handleEntityContext(e, 'company', c, project.id)}
                          fallback={<Badge fontSize="9px" colorScheme="blue" variant="subtle" cursor="pointer"
                            onClick={(e) => { e.stopPropagation(); setQuickViewCompany(c.id); }}
                            onContextMenu={(e) => handleEntityContext(e, 'company', c, project.id)}>{c.name}</Badge>} />
                      </Tooltip>
                    ) : (
                      <Badge key={c.id} fontSize="9px" colorScheme="blue" variant="subtle" cursor="pointer"
                        onClick={(e) => { e.stopPropagation(); setQuickViewCompany(c.id); }}
                        onContextMenu={(e) => handleEntityContext(e, 'company', c, project.id)}>{c.name}</Badge>
                    )
                  ))}
                </Flex>
              )}
            </Td>}
            {v('contact') && <Td className="data-cell" onContextMenu={(e) => {
              const cts = project.contacts || [];
              if (cts.length === 1) handleEntityContext(e, 'contact', cts[0], project.id);
              else if (cts.length === 0) { e.preventDefault(); openAddEntity('contact', project.id); }
            }}>
              {(project.contacts || []).length === 0 ? (
                <Text fontSize="11px" color="gray.400" cursor="pointer" onClick={() => openAddEntity('contact', project.id)}>+ Contact</Text>
              ) : (
                <Flex gap={0.5} flexWrap="wrap" px={1} py={0.5} align="center">
                  {(project.contacts || []).map(c => (
                    showAvatars && c.avatar_url ? (
                      <Tooltip key={c.id} label={`${c.first_name} ${c.last_name || ''}`} fontSize="xs">
                        <Image src={`/api/files/${c.avatar_url}`} alt={c.first_name} w={`${logoSize}px`} h={`${logoSize}px`} borderRadius="full" objectFit="cover"
                          cursor="pointer"
                          onClick={(e) => { e.stopPropagation(); setQuickViewContact(c.id); }}
                          onContextMenu={(e) => handleEntityContext(e, 'contact', c, project.id)}
                          fallback={<Badge fontSize="9px" colorScheme="purple" variant="subtle" cursor="pointer"
                            onClick={(e) => { e.stopPropagation(); setQuickViewContact(c.id); }}
                            onContextMenu={(e) => handleEntityContext(e, 'contact', c, project.id)}>{c.first_name} {c.last_name || ''}</Badge>} />
                      </Tooltip>
                    ) : (
                      <Badge key={c.id} fontSize="9px" colorScheme="purple" variant="subtle" cursor="pointer"
                        onClick={(e) => { e.stopPropagation(); setQuickViewContact(c.id); }}
                        onContextMenu={(e) => handleEntityContext(e, 'contact', c, project.id)}>{c.first_name} {c.last_name || ''}</Badge>
                    )
                  ))}
                </Flex>
              )}
            </Td>}
            {v('action_type') && <Td className="data-cell"><ActionTypeSelect projectId={project.id} /></Td>}
          </>
        ) : (
          <Td className="data-cell" colSpan={['drag','done','wc','project','project_date','deadline','client','contact','action_type'].filter(k => v(k)).length} borderRight="1px" borderColor="gray.100">
            <Text fontSize="10px" color="gray.300" pl={2}>↳</Text>
          </Td>
        )}

        {/* Task-level cells */}
        {v('task') && <Td className="data-cell" maxW="200px" onContextMenu={(e) => {
          e.preventDefault(); setTaskMenu({ x: e.clientX, y: e.clientY, task, project });
        }}>
          <Tooltip label={task.description || 'No description'} fontSize="xs" placement="top" hasArrow maxW="300px" whiteSpace="pre-wrap" isDisabled={!task.description}>
            <Box cursor="pointer" onClick={() => openTask(project, task)} minH="20px" fontSize="12px"
              color={(task.deleted_at || task.is_complete) ? 'gray.400' : (task.task_title ? 'gray.800' : 'gray.400')} px={1} py={0.5}>
              <Flex align="center" gap={1}>
                <Text noOfLines={1} flex={1}>{task.task_title || '—'}</Text>
                {/* Assigned users */}
                {task.assigned_users_json && (() => {
                  let users = [];
                  try { users = typeof task.assigned_users_json === 'string' ? JSON.parse(task.assigned_users_json) : task.assigned_users_json; } catch(e) {}
                  return users.slice(0, 3).map(u => (
                    <Tooltip key={u.user_id} label={u.display_name} fontSize="xs" hasArrow>
                      <Box as="span" display="inline-flex" alignItems="center" justifyContent="center"
                        w="16px" h="16px" borderRadius="full" bg="teal.500" color="white"
                        fontSize="7px" fontWeight="800" flexShrink={0} lineHeight={1}>
                        {(u.display_name || '?').slice(0,1).toUpperCase()}
                      </Box>
                    </Tooltip>
                  ));
                })()}
                {task.source_task_id && (
                  <Tooltip label="Clone — right-click to view history" fontSize="xs" hasArrow>
                    <Box as="span" display="inline-flex" alignItems="center" flexShrink={0}
                      fontSize="7px" fontWeight="700" color="purple.400" border="1px solid" borderColor="purple.200"
                      borderRadius="3px" px="3px" py="1px" lineHeight={1} letterSpacing="0.03em"
                      cursor="context-menu">
                      CLONE
                    </Box>
                  </Tooltip>
                )}
              </Flex>
            </Box>
          </Tooltip>
        </Td>}
        {v('task_deadline') && <Td className="data-cell">
          <InlineEdit value={task.deadline_date ? task.deadline_date.split('T')[0] : ''} type="date"
            onSave={(val) => handleTaskUpdate(task.id, { deadline_date: val || null })} />
        </Td>}
        {v('task_companies') && <Td className="data-cell">
          <Flex gap={0.5} flexWrap="wrap">
            {(task.companies || []).map(c => <Badge key={c.id} fontSize="8px" colorScheme="blue" variant="subtle">{c.name}</Badge>)}
          </Flex>
        </Td>}
        {v('task_contacts') && <Td className="data-cell">
          <Flex gap={0.5} flexWrap="wrap">
            {(task.contacts || []).map(c => <Badge key={c.id} fontSize="8px" colorScheme="purple" variant="subtle">{c.first_name}</Badge>)}
          </Flex>
        </Td>}
        {v('held') && <Td className="data-cell" textAlign="center">
          <Checkbox size="sm" isChecked={!!task.is_held} colorScheme="red"
            onChange={() => { setHoldTask(task); holdDisclosure.onOpen(); }} />
        </Td>}
        {v('complete') && <Td className="data-cell" textAlign="center">
          <Checkbox size="sm" isChecked={!!task.is_complete} colorScheme="green"
            onChange={(e) => handleTaskUpdate(task.id, { is_complete: e.target.checked })} />
        </Td>}

        {/* Icon cells */}
        {v('calendar') && <Td textAlign="center" px={1}>
          <Box display="inline-block" position="relative">
            <Tooltip label="Calendar" fontSize="xs"><IconButton icon={<FiCalendar size={18} />} size="sm" variant="ghost" colorScheme="teal"
              sx={!task.calendar_count ? { color: dimColor, '&:hover': { color: 'teal.500' } } : {}}
              onClick={() => openCalendar(project.id, task.id, project.tasks)} aria-label="Calendar" /></Tooltip>
            {task.calendar_count > 0 && <Text fontSize="8px" fontWeight="700" color="teal.600" position="absolute" top="-4px" right="2px">{task.calendar_count}</Text>}
          </Box>
        </Td>}

        {v('bookings') && <Td textAlign="center" px={1}>
          <Tooltip label="Resource Bookings" fontSize="xs"><IconButton icon={<FiBookOpen size={16} />} size="sm" variant="ghost" colorScheme="cyan"
            sx={!task.booking_count ? { color: dimColor, '&:hover': { color: 'cyan.500' } } : {}}
            onClick={() => openBookings(project.id, task.id)} aria-label="Bookings" /></Tooltip>
        </Td>}
        {v('planned_visits') && <Td textAlign="center" px={1}>
          <Box position="relative" display="inline-block">
            <Tooltip label="Planned Visits" fontSize="xs"><IconButton icon={<FiUserCheck size={16} />} size="sm" variant="ghost" colorScheme="purple"
              sx={!task.planned_visit_count ? { color: dimColor, '&:hover': { color: 'purple.500' } } : {}}
              onClick={() => openPlannedVisits(task.id, task.task_title, project.id)} aria-label="Planned Visits" /></Tooltip>
            {task.planned_visit_count > 0 && <Text fontSize="8px" fontWeight="700" color="purple.600" position="absolute" top="-4px" right="2px">{task.planned_visit_count}</Text>}
          </Box>
        </Td>}
        {v('diary') && <Td textAlign="center" px={1}>
          <Tooltip label="Request an Appointment" fontSize="xs">
            <IconButton icon={<FiCalendar size={15} />} size="sm" variant="ghost" colorScheme="teal"
              sx={{ color: dimColor, '&:hover': { color: 'teal.500' } }}
              onClick={() => openDiaryForTask(project, task)} aria-label="Request Appointment" />
          </Tooltip>
        </Td>}
        {v('progress') && <Td textAlign="center" px={0}>
          {(task.completion_percentage || 0) > 0 ? (
            <Tooltip label="Sub-Tasks / To-Do" fontSize="xs">
              <Text fontSize="10px" fontWeight="800" cursor="pointer"
                color={(task.completion_percentage || 0) >= 100 ? 'green.500' : (task.completion_percentage || 0) >= 50 ? 'blue.500' : 'orange.500'}
                onClick={() => { setProgressTaskId(task.id); setProgressTaskTitle(task.task_title); progressDisclosure.onOpen(); }}
                _hover={{ textDecoration: 'underline' }}>
                {task.completion_percentage}%
              </Text>
            </Tooltip>
          ) : (
            <Tooltip label="Sub-Tasks / To-Do" fontSize="xs">
              <IconButton icon={<FiClock size={14} />} size="xs" variant="ghost" colorScheme="teal"
                sx={{ color: dimColor, '&:hover': { color: 'teal.500' } }}
                onClick={() => { setProgressTaskId(task.id); setProgressTaskTitle(task.task_title); progressDisclosure.onOpen(); }}
                aria-label="Sub-Tasks" />
            </Tooltip>
          )}
        </Td>}
        {v('clock') && <Td textAlign="center" px={0}>
          <Tooltip label="Clock On" fontSize="xs">
            <IconButton icon={<FiPlay size={14} />} size="xs" variant="ghost" colorScheme="green"
              onClick={() => { setWorkSessionTask({ taskId: task.id, taskTitle: task.task_title, projectId: project.id }); workSessionDisclosure.onOpen(); }}
              aria-label="Work Session" />
          </Tooltip>
        </Td>}
        {v('mileage') && <Td textAlign="center" px={1}>
          <Box display="inline-block" position="relative">
            <Tooltip label="Mileage" fontSize="xs"><IconButton icon={<FiTruck size={18} />} size="sm" variant="ghost"
              colorScheme="cyan"
              sx={!task.mileage_count ? { color: dimColor, '&:hover': { color: 'cyan.500' } } : {}}
              onClick={() => openMileage(project.id, task.id)} aria-label="Mileage" /></Tooltip>
            {task.mileage_count > 0 && <Text fontSize="8px" fontWeight="700" color="cyan.600" position="absolute" top="-4px" right="2px">{task.mileage_count}</Text>}
          </Box>
        </Td>}
        {v('docs') && <Td textAlign="center" px={1}>
          <Box display="inline-block" position="relative">
            <Tooltip label="Documents" fontSize="xs"><IconButton icon={<FiFolder size={18} />} size="sm" variant="ghost"
              colorScheme="teal"
              sx={!task.doc_count ? { color: dimColor, '&:hover': { color: 'teal.500' } } : {}}
              onClick={() => openDocs(project.id, task.id)} aria-label="Documents" /></Tooltip>
            {task.doc_count > 0 && <Text fontSize="8px" fontWeight="700" color="teal.600" position="absolute" top="-4px" right="2px">{task.doc_count}</Text>}
          </Box>
        </Td>}
        {v('procedures') && <Td textAlign="center" px={1} cursor="pointer"
          onClick={e => { e.stopPropagation(); openProcSafe(task.id, 'procedures'); }}>
          <Box display="inline-block" position="relative">
            <Tooltip label="Procedures" fontSize="xs"><IconButton icon={<FiClipboard size={17} />} size="sm" variant="ghost"
              colorScheme="teal"
              sx={!task.procedure_count ? { color: dimColor, '&:hover': { color: 'teal.500' } } : {}}
              onClick={e => { e.stopPropagation(); openProcSafe(task.id, 'procedures'); }} aria-label="Procedures" /></Tooltip>
            {/* pointerEvents none: the badge must never intercept the click */}
            {task.procedure_count > 0 && <Text fontSize="8px" fontWeight="700" color="teal.600" position="absolute" top="-4px" right="2px" pointerEvents="none">{task.procedure_count}</Text>}
          </Box>
        </Td>}
        {v('passwords') && <Td textAlign="center" px={1} cursor="pointer"
          onClick={e => { e.stopPropagation(); openProcSafe(task.id, 'passwords'); }}>
          <Box display="inline-block" position="relative">
            <Tooltip label="Password Safe" fontSize="xs"><IconButton icon={<FiKey size={17} />} size="sm" variant="ghost"
              colorScheme="purple"
              sx={!task.password_count ? { color: dimColor, '&:hover': { color: 'purple.500' } } : {}}
              onClick={e => { e.stopPropagation(); openProcSafe(task.id, 'passwords'); }} aria-label="Password Safe" /></Tooltip>
            {task.password_count > 0 && <Text fontSize="8px" fontWeight="700" color="purple.600" position="absolute" top="-4px" right="2px" pointerEvents="none">{task.password_count}</Text>}
          </Box>
        </Td>}
        {v('uploads') && <Td textAlign="center" px={1} cursor="pointer"
          onClick={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('forgebos:open-upload-portals', { detail: { taskId: task.id, name: task.task_title || 'Task' } })); }}>
          <Box display="inline-block" position="relative">
            {/* Illuminated baby blue when this task has portals; portal
                count sits top-LEFT, total uploaded files top-RIGHT. */}
            <Tooltip label="Client Upload Portals" fontSize="xs"><IconButton icon={<FiUploadCloud size={17} />} size="sm" variant="ghost"
              colorScheme="blue"
              sx={task.upload_portal_count > 0
                ? { color: 'blue.300', '&:hover': { color: 'blue.500' } }
                : { color: dimColor, '&:hover': { color: 'blue.500' } }}
              onClick={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('forgebos:open-upload-portals', { detail: { taskId: task.id, name: task.task_title || 'Task' } })); }}
              aria-label="Client Upload Portals" /></Tooltip>
            {/* Counters sit at vertical centre, tight to the cloud:
                portals on the left, total files on the right. */}
            {/* Top corners, matching the procedures/passwords badge
                pattern: portal count top-left, file total top-right —
                clear of the glyph so both stay readable. */}
            {task.upload_portal_count > 0 && <Text fontSize="8px" fontWeight="700" color="blue.400" position="absolute" top="-4px" left="2px" pointerEvents="none">{task.upload_portal_count}</Text>}
            {task.upload_file_count > 0 && <Text fontSize="8px" fontWeight="700" color="blue.600" position="absolute" top="-4px" right="2px" pointerEvents="none">{task.upload_file_count}</Text>}
          </Box>
        </Td>}
        {v('incidents') && <Td textAlign="center" px={1}>
          <Box display="inline-block" position="relative">
            <Tooltip label="Incident Log" fontSize="xs">
              <IconButton
                icon={<FiAlertCircle size={18} />}
                size="sm" variant="ghost"
                sx={task.incident_open_count > 0
                  ? { color: '#E53E3E', '&:hover': { color: '#C53030' } }
                  : task.incident_count > 0
                  ? { color: '#38A169', '&:hover': { color: '#276749' } }
                  : { color: dimColor, '&:hover': { color: 'gray.500' } }}
                onClick={() => openIncident(project.id, task.id)} aria-label="Incident Log" />
            </Tooltip>
            {task.incident_count > 0 && (
              <Text fontSize="8px" fontWeight="700"
                color={task.incident_open_count > 0 ? '#E53E3E' : '#38A169'}
                position="absolute" top="-4px" right="2px">
                {task.incident_count}
              </Text>
            )}
          </Box>
        </Td>}
        {v('feedback') && <Td textAlign="center" px={1}>
          <Box display="inline-block" position="relative">
            <Tooltip label={`Client Feedback${task.feedback_unseen > 0 ? ` — ${task.feedback_unseen} new response${task.feedback_unseen !== 1 ? 's' : ''}` : ''}`} fontSize="xs">
              <IconButton icon={<FiMessageSquare size={18} />} size="sm" variant="ghost"
                colorScheme="purple"
                onClick={() => openFeedback(project.id, task.id)} aria-label="Feedback"
                sx={task.feedback_unseen > 0 ? {
                  animation: 'feedbackPulse 2s ease-in-out infinite',
                  '@keyframes feedbackPulse': { '0%,100%': { color: '#c05600' }, '50%': { color: '#EE7203' } },
                } : !task.feedback_count ? { color: dimColor, '&:hover': { color: 'purple.500' } } : {}} /></Tooltip>
            {(task.feedback_count > 0 || task.feedback_unseen > 0) && (
              <Text fontSize="8px" fontWeight="700"
                color={task.feedback_unseen > 0 ? '#EE7203' : 'purple.600'}
                position="absolute" top="-4px" right="2px">
                {task.feedback_unseen > 0 ? task.feedback_unseen : task.feedback_count}
              </Text>
            )}
          </Box>
        </Td>}
        {v('comms') && <Td textAlign="center" px={1}>
          <Box display="inline-block" position="relative">
            <Tooltip label="Comms" fontSize="xs"><IconButton icon={<FiMail size={18} />} size="sm" variant="ghost"
              colorScheme={task.id === highlightTaskId ? 'orange' : taskUnread[task.id] > 0 ? 'orange' : 'blue'}
              onClick={() => openComms(project.id, task.id)} aria-label="Comms"
              sx={task.id === highlightTaskId ? {
                animation: 'emailHighlight 1s ease-in-out infinite',
                '@keyframes emailHighlight': {
                  '0%,100%': { color: '#c05600', transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(238,114,3,0)' },
                  '50%': { color: '#EE7203', transform: 'scale(1.2)', boxShadow: '0 0 0 6px rgba(238,114,3,0.2)' },
                },
              } : taskUnread[task.id] > 0 ? {
                animation: 'commsPulse 2s ease-in-out infinite',
                '@keyframes commsPulse': { '0%,100%': { color: '#c05600' }, '50%': { color: highlightColor } },
              } : (!task.comm_count ? { color: dimColor, '&:hover': { color: 'blue.500' } } : {})} /></Tooltip>
            {(task.comm_count > 0 || taskUnread[task.id] > 0 || task.id === highlightTaskId) && <Text fontSize="8px" fontWeight="700" color={task.id === highlightTaskId ? '#EE7203' : taskUnread[task.id] > 0 ? '#EE7203' : 'blue.600'} position="absolute" top="-4px" right="2px">{task.comm_count + (taskUnread[task.id] || 0)}</Text>}
          </Box>
        </Td>}
        {v('notes') && <Td textAlign="center" px={1}>
          <Box display="inline-block" position="relative">
            <Tooltip label="Notes" fontSize="xs"><IconButton icon={<FiFileText size={18} />} size="sm" variant="ghost" colorScheme="purple"
              sx={!task.notes_count ? { color: dimColor, '&:hover': { color: 'purple.500' } } : {}}
              onClick={() => openNotes(project.id, task.id)} aria-label="Notes" /></Tooltip>
            {task.notes_count > 0 && <Text fontSize="8px" fontWeight="700" color="purple.600" position="absolute" top="-4px" right="2px">{task.notes_count}</Text>}
          </Box>
        </Td>}
        {v('suppliers') && (() => { const supCount = task.supplier_count || 0; return <Td textAlign="center" px={1}>
          <Box display="inline-block" position="relative">
            <Tooltip label="Suppliers" fontSize="xs"><IconButton icon={<FiShoppingCart size={18} />} size="sm" variant="ghost" colorScheme="orange"
              sx={!supCount ? { color: dimColor, '&:hover': { color: 'orange.500' } } : {}}
              onClick={() => openSuppliers(project.id, task.id, project.tasks)} aria-label="Suppliers" /></Tooltip>
            {supCount > 0 && <Text fontSize="8px" fontWeight="700" color="orange.600" position="absolute" top="-4px" right="2px">{supCount}</Text>}
          </Box>
        </Td>; })()}
        {v('prospect') && <Td textAlign="center" px={0}>
          {project.engagement_status_name && /cold|warm|quotation|prospect|lead/i.test(project.engagement_status_name) ? (
            <Tooltip label="Edit Prospect" fontSize="xs">
              <IconButton icon={<FiEdit2 size={14} />} size="xs" variant="ghost" colorScheme="purple"
                onClick={() => { setProspectEditData({ company_name: project.company_name || '', company_id: project.company_id, engagement_status_id: project.engagement_status_id || '', lead_source_id: project.lead_source_id || '', business_summary: task.task_title || '', outreach_ideas: task.description || '' }); prospectDisclosure.onOpen(); }} aria-label="Edit" />
            </Tooltip>
          ) : null}
        </Td>}
        {v('inv_in') && <Td textAlign="center" px={0} w={cw('inv_in')} borderLeft={showTotals ? '1px solid' : 'none'} borderColor="gray.200" style={{ background:'white' }}>
          <Flex direction="column" align="center" justify="center" gap={0}>
            <Box display="inline-block" position="relative">
              <Tooltip label="£ In — Client invoices" fontSize="xs">
                {task.inv_in_count > 0 ? (
                  <IconButton icon={<Text fontSize="11px" fontWeight="800">£</Text>} size="xs" variant="solid" bg="green.500" color="white" _hover={{ bg: 'green.600' }} borderRadius="sm" onClick={() => openInvoice(project.id, task.id)} aria-label="Inv In" h="18px" minW="18px" />
                ) : (
                  <IconButton icon={<Text fontSize="11px" fontWeight="800">£</Text>} size="xs" variant="ghost" borderRadius="sm"
                    sx={{ color: dimColor, '&:hover': { color: 'green.500' } }} h="18px" minW="18px"
                    onClick={() => openInvoice(project.id, task.id)} aria-label="Inv In" />
                )}
              </Tooltip>
              {task.inv_in_count > 0 && <Text fontSize="7px" fontWeight="700" color="green.700" position="absolute" top="-3px" right="-5px">{task.inv_in_count}</Text>}
            </Box>
            {showTotals && (() => {
              // Use currency-filtered totals: if only GBP selected, use gbp columns; otherwise use all
              const useGbpOnly = invCurrencies.length === 1 && invCurrencies.includes('gbp') && !invCurrencies.includes('all');
              const total = parseFloat(useGbpOnly ? (task.inv_in_gbp || 0) : (task.inv_in_total || 0));
              const fmt = (v) => '£' + v.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
              if (total === 0) return <Text mt={1} fontSize="9px" color="gray.300" lineHeight="1">{fmt(0)}</Text>;
              const paid = parseFloat(task.inv_in_paid || 0);
              const quoted = parseFloat(task.inv_in_quoted || 0);
              const unpaid = total - paid - quoted;
              return (
                <Flex direction="column" lineHeight="1.3" align="center" mt={1}>
                  {paid > 0 && <Text fontSize="9px" fontWeight="700" color="green.700" whiteSpace="nowrap">{fmt(paid)}</Text>}
                  {unpaid > 0 && <Text fontSize="9px" fontWeight="600" color="gray.500" whiteSpace="nowrap">{fmt(unpaid)}</Text>}
                  {quoted > 0 && <Text fontSize="9px" fontWeight="600" color="blue.600" whiteSpace="nowrap">{fmt(quoted)}</Text>}
                </Flex>
              );
            })()}
          </Flex>
        </Td>}
        {v('inv_out') && <Td textAlign="center" px={0} w={cw('inv_out')} borderLeft={showTotals ? '1px solid' : 'none'} borderColor="gray.200" style={{ background:'white' }}>
          <Flex direction="column" align="center" justify="center" gap={0}>
            <Box display="inline-block" position="relative">
              <Tooltip label="£ Out — Supplier costs" fontSize="xs">
                {task.inv_out_count > 0 ? (
                  <IconButton icon={<Text fontSize="11px" fontWeight="800">£</Text>} size="xs" variant="solid" bg="red.500" color="white" _hover={{ bg: 'red.600' }} borderRadius="sm" onClick={() => openInvoiceIn(project.id, task.id)} aria-label="Inv Out" h="18px" minW="18px" />
                ) : (
                  <IconButton icon={<Text fontSize="11px" fontWeight="800">£</Text>} size="xs" variant="ghost" borderRadius="sm"
                    sx={{ color: dimColor, '&:hover': { color: 'red.500' } }} h="18px" minW="18px"
                    onClick={() => openInvoiceIn(project.id, task.id)} aria-label="Inv Out" />
                )}
              </Tooltip>
              {task.inv_out_count > 0 && <Text fontSize="7px" fontWeight="700" color="red.600" position="absolute" top="-3px" right="-5px">{task.inv_out_count}</Text>}
            </Box>
            {showTotals && (() => {
              const useGbpOnly = invCurrencies.length === 1 && invCurrencies.includes('gbp') && !invCurrencies.includes('all');
              const total = parseFloat(useGbpOnly ? (task.inv_out_gbp || 0) : (task.inv_out_total || 0));
              const fmt = (v) => '£' + v.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
              if (total === 0) return <Text mt={1} fontSize="9px" color="gray.300" lineHeight="1">{fmt(0)}</Text>;
              const paid = parseFloat(task.inv_out_paid || 0);
              const quoted = parseFloat(task.inv_out_quoted || 0);
              const unpaid = total - paid - quoted;
              return (
                <Flex direction="column" lineHeight="1.3" align="center" mt={1}>
                  {paid > 0 && <Text fontSize="9px" fontWeight="600" color="gray.500" whiteSpace="nowrap">{fmt(paid)}</Text>}
                  {unpaid > 0 && <Text fontSize="9px" fontWeight="700" color="red.600" whiteSpace="nowrap">{fmt(unpaid)}</Text>}
                  {quoted > 0 && <Text fontSize="9px" fontWeight="600" color="blue.600" whiteSpace="nowrap">{fmt(quoted)}</Text>}
                </Flex>
              );
            })()}
          </Flex>
        </Td>}
        {v('vat') && <Td textAlign="center" px={0}
          w={showTotals ? cw('vat') : 0}
          borderLeft={showTotals ? '1px solid' : 'none'} borderColor="gray.200"
          style={{ background:'white', overflow: 'hidden', maxWidth: showTotals ? undefined : 0, padding: showTotals ? undefined : 0 }}>
          {showTotals && (() => {
            const useGbpOnly = invCurrencies.length === 1 && invCurrencies.includes('gbp') && !invCurrencies.includes('all');
            const vatIn  = parseFloat(useGbpOnly ? (task.inv_in_vat_gbp  || task.inv_in_vat  || 0) : (task.inv_in_vat  || 0));
            const vatOut = parseFloat(useGbpOnly ? (task.inv_out_vat_gbp || task.inv_out_vat || 0) : (task.inv_out_vat || 0));
            // Optionally add BBX cash VAT liability/claim
            const barterVatIn  = invIncludeBarterVat ? parseFloat(task.inv_in_barter_vat  || 0) : 0;
            const barterVatOut = invIncludeBarterVat ? parseFloat(task.inv_out_barter_vat || 0) : 0;
            const vatNet = (vatIn + barterVatIn) - (vatOut + barterVatOut);
            const fmt = (v) => '£' + Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            if (vatNet === 0 && vatIn === 0 && barterVatIn === 0) return <Text fontSize="9px" color="gray.300" lineHeight="1">—</Text>;
            return (
              <Text fontSize="9px" fontWeight="700" lineHeight="1"
                color={vatNet > 0 ? 'red.600' : vatNet < 0 ? 'green.600' : 'gray.400'}>
                {vatNet > 0 ? '' : vatNet < 0 ? '+' : ''}{fmt(vatNet)}
              </Text>
            );
          })()}
        </Td>}
        {v('bal') && <Td textAlign="center" px={0}
          w={showTotals ? cw('bal') : 0}
          borderLeft={showTotals ? '1px solid' : 'none'} borderColor="gray.200"
          style={{ background:'white', overflow: 'hidden', maxWidth: showTotals ? undefined : 0, padding: showTotals ? undefined : 0 }}>
          {(() => {
            const totalIn  = parseFloat(task.inv_in_total  || 0);
            const totalOut = parseFloat(task.inv_out_total || 0);
            const vatNet   = parseFloat(task.inv_in_vat || 0) - parseFloat(task.inv_out_vat || 0);
            const totalBal = totalIn - totalOut - vatNet;
            const fmt = (v) => '£' + Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            const color = totalBal > 0 ? 'green.700' : totalBal < 0 ? 'red.700' : 'gray.400';
            return <Text fontSize="9px" fontWeight="700" color={color} lineHeight="1">{totalBal < 0 ? '-' : ''}{fmt(totalBal)}</Text>;
          })()}
        </Td>}
        {v('delete') && <Td w={cw('delete')} style={{ background:'white' }} />}

      </Tr>
    );
  };

  // ── Invoice status filter helpers ────────────────────────────────────────
  const IN_FILTER_OPTS = [
    { value: 'to_invoice',            label: 'To Invoice' },
    { value: 'created_not_sent',      label: 'Created (Not Sent)' },
    { value: 'sent_for_payment',      label: 'Sent for Payment' },
    { value: 'invoiced_within_terms', label: 'Within Terms' },
    { value: 'paid_in_part',          label: 'Part Paid' },
    { value: 'paid_in_full',          label: 'Paid in Full' },
    { value: 'overdue',               label: 'Overdue' },
    { value: 'very_overdue',          label: 'Very Overdue' },
    { value: 'written_off',           label: 'Written Off' },
    { value: 'send_for_debt_collection', label: 'Debt Collection' },
    { value: 'quotation',             label: 'Quotation (Sent)' },
    { value: 'quotation_not_sent',    label: 'Quotation (Not Sent)' },
    { value: 'po_requested',          label: 'PO Requested' },
    { value: 'po_issued',             label: 'PO Issued' },
    { value: 'receipt',               label: 'Receipt' },
    { value: 'opportunity_value',     label: 'Opportunity Value' },
    { value: 'credited',              label: 'Credited' },
    { value: 'obsolete',              label: 'Obsolete' },
    { value: 'superseded_quote',      label: 'Superseded Quote' },
    { value: 'deleted',               label: 'Deleted' },
    { value: '__overdue_14',          label: '⚠ Overdue ≤14 days' },
    { value: '__overdue_30',          label: '⚠ Overdue ≤30 days' },
    { value: '__overdue_60',          label: '⚠ Overdue ≤60 days' },
    { value: '__overdue_gt60',         label: '⚠ Overdue >60 days' },
  ];
  const OUT_FILTER_OPTS = [
    { value: 'recv_do_not_pay',       label: 'Do NOT Pay Yet' },
    { value: 'recv_pay_within_terms', label: 'PAY WITHIN TERMS' },
    { value: 'recv_assign_po',        label: 'Assign PO Number' },
    { value: 'recv_refused',          label: 'REFUSED' },
    { value: 'recv_overdue',          label: 'OVERDUE' },
    { value: 'recv_receipt',          label: 'Receipt for Payment' },
    { value: 'recv_credited',         label: 'Credited by Supplier' },
    { value: 'paid_in_full',          label: 'Paid in Full' },
    { value: 'quotation',             label: 'Quotation (Sent)' },
    { value: 'quotation_not_sent',    label: 'Quotation (Not Sent)' },
    { value: 'po_requested',          label: 'PO Requested' },
    { value: 'po_issued',             label: 'PO Issued' },
    { value: 'credited',              label: 'Credited' },
    { value: 'obsolete',              label: 'Obsolete' },
    { value: 'superseded_quote',      label: 'Superseded Quote' },
    { value: 'deleted',               label: 'Deleted' },
    { value: '__overdue_14',          label: '⚠ Overdue ≤14 days' },
    { value: '__overdue_30',          label: '⚠ Overdue ≤30 days' },
    { value: '__overdue_60',          label: '⚠ Overdue ≤60 days' },
    { value: '__overdue_gt60',         label: '⚠ Overdue >60 days' },
  ];

  const toggleStatus = (arr, setArr, val) =>
    setArr(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  // Per-group totals calculator
  // Check if a task falls within the date filter range
  const taskPassesDateFilter = (task) => {
    if (!invDateFrom && !invDateTo) return true; // any to any = no filter
    const field = invDateField === 'issue' ? 'issue' : 'due';
    // Check IN invoices (direction='out' in DB)
    const inMin = task[`inv_in_min_${field}`];
    const inMax = task[`inv_in_max_${field}`];
    // Check OUT invoices (direction='in' in DB)
    const outMin = task[`inv_out_min_${field}`];
    const outMax = task[`inv_out_max_${field}`];

    const hasInInvoice = inMin || inMax;
    const hasOutInvoice = outMin || outMax;
    if (!hasInInvoice && !hasOutInvoice) return false; // no invoices = doesn't match

    const checkRange = (minDate, maxDate) => {
      if (!minDate && !maxDate) return false;
      const from = invDateFrom || '0000-01-01';
      const to   = invDateTo   || '9999-12-31';
      // The invoice overlaps the range if its max >= from AND min <= to
      const effectiveMin = minDate || maxDate;
      const effectiveMax = maxDate || minDate;
      return effectiveMax >= from && effectiveMin <= to;
    };
    return checkRange(inMin, inMax) || checkRange(outMin, outMax);
  };

  // Get the primary row group id for a project
  const getPrimaryGroupId = (project) => {
    const primary = (project.row_groups || []).find(g => g.is_primary);
    return primary?.row_group_id || project.row_group_id;
  };

  const calcGroupTotals = (groupProjects) => {
    let tInPaid=0, tInUnpaid=0, tInQuoted=0, tOutPaid=0, tOutUnpaid=0, tOutQuoted=0;
    let tVatIn=0, tVatOut=0;
    groupProjects.forEach(p => {
      if (hideDone && !!p.is_done) return;
      if (countOnlyPrimary && p._display_group_id !== getPrimaryGroupId(p)) return;
      (p.tasks || []).forEach(t => {
        if (hideDone && !!t.is_complete) return;
        if (hideHeld && !!t.is_held) return;
        const ip=parseFloat(t.inv_in_paid||0), iq=parseFloat(t.inv_in_quoted||0);
        const op=parseFloat(t.inv_out_paid||0), oq=parseFloat(t.inv_out_quoted||0);
        tInPaid+=ip; tInQuoted+=iq; tInUnpaid+=parseFloat(t.inv_in_total||0)-ip-iq;
        tOutPaid+=op; tOutQuoted+=oq; tOutUnpaid+=parseFloat(t.inv_out_total||0)-op-oq;
        tVatIn  += parseFloat(t.inv_in_vat  || 0);
        tVatOut += parseFloat(t.inv_out_vat || 0);
      });
    });
    const totalIn  = tInPaid+tInUnpaid+tInQuoted;
    const totalOut = tOutPaid+tOutUnpaid+tOutQuoted;
    const vatNet   = tVatIn - tVatOut; // positive = liability, negative = claim
    return { tInPaid, tInUnpaid, tInQuoted, tOutPaid, tOutUnpaid, tOutQuoted,
      totalIn, totalOut, vatNet, tVatIn, tVatOut };
  };

  const fmtMny = (v) => '£' + v.toLocaleString('en-GB', { minimumFractionDigits:0, maximumFractionDigits:0 });

  return (
    <Box>
      <Box bg="white" style={{ position: 'relative' }}>
        <Table size="sm" variant="crm">
          <Thead style={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#1a202c' }}>
            <Tr style={{ backgroundColor: '#1a202c' }}>
              {v('drag') && <Th w={cw('drag')}></Th>}
              {v('done') && <Th w={cw('done')} textAlign="center" cursor="pointer" userSelect="none" p={0}
                onClick={() => setHideDone(!hideDone)} _hover={{ opacity: 0.8 }}
                title={hideDone ? 'Hiding completed — click to show' : 'Click to hide completed'}>
                <Box display="inline-block" px={2} py={0.5} borderRadius="4px" fontSize="10px" fontWeight="700"
                  bg={hideDone ? '#EE7203' : 'transparent'} color="white"
                  border="2px solid" borderColor={hideDone ? '#EE7203' : 'whiteAlpha.600'}>
                  Done
                </Box>
              </Th>}
              {v('wc') && <Th w={cw('wc')} textAlign="center">WC</Th>}
              {v('project') && <Th w={cw('project')}>Project</Th>}
              {v('project_date') && <Th w={cw('project_date')}>Date</Th>}
              {v('deadline') && <Th w={cw('deadline')}>Deadline</Th>}
              {v('client') && <Th w={cw('client')}>Client</Th>}
              {v('contact') && <Th w={cw('contact')}>Contact</Th>}
              {v('action_type') && <Th w={cw('action_type')}>Type of Work</Th>}
              {v('task') && <Th>Task</Th>}
              {v('task_deadline') && <Th w={cw('task_deadline')}>Task Deadline</Th>}
              {v('task_companies') && <Th w={cw('task_companies')}>Task Co.</Th>}
              {v('task_contacts') && <Th w={cw('task_contacts')}>Task Contact</Th>}
              {v('held') && <Th w={cw('held')} textAlign="center" cursor="pointer" userSelect="none" p={0}
                onClick={() => setHideHeld(!hideHeld)} _hover={{ opacity: 0.8 }}
                title={hideHeld ? 'Hiding held — click to show' : 'Click to hide held'}>
                <Box display="inline-block" px={2} py={0.5} borderRadius="4px" fontSize="10px" fontWeight="700"
                  bg={hideHeld ? '#EE7203' : 'transparent'} color="white"
                  border="2px solid" borderColor={hideHeld ? '#EE7203' : 'whiteAlpha.600'}>
                  Held
                </Box>
              </Th>}
              {v('complete') && <Th w={cw('complete')} textAlign="center"><Tooltip label="Task Complete" fontSize="xs"><Box cursor="default"><FiCheck size={12} /></Box></Tooltip></Th>}
              {v('calendar') && <Th w={cw('calendar')}></Th>}
              {v('bookings') && <Th w={cw('bookings')}></Th>}
              {v('planned_visits') && <Th w={cw('planned_visits')}></Th>}
              {v('diary') && <Th w={cw('diary')}></Th>}
              {v('progress') && <Th w={cw('progress')}></Th>}
              {v('clock') && <Th w={cw('clock')}></Th>}
              {v('mileage') && <Th w={cw('mileage')}></Th>}
              {v('docs') && <Th w={cw('docs')}></Th>}
              {v('procedures') && <Th w={cw('procedures')}></Th>}
              {v('passwords') && <Th w={cw('passwords')}></Th>}
              {v('incidents') && <Th w={cw('incidents')}></Th>}
              {v('feedback') && <Th w={cw('feedback')}></Th>}
              {v('comms') && <Th w={cw('comms')}></Th>}
              {v('notes') && <Th w={cw('notes')}></Th>}
              {v('suppliers') && <Th w={cw('suppliers')}></Th>}
              {v('prospect') && <Th w={cw('prospect')}></Th>}
              {v('inv_in') && <Th w={cw('inv_in')} textAlign="center" p={0} borderLeft={showTotals ? '1px solid' : 'none'} borderColor="gray.200" style={{ background:'#1a202c' }}>
                <Flex direction="column" align="center" lineHeight="1" cursor="pointer" onClick={() => { setShowTotals(!showTotals); if (!showTotals) setShowTotalsModal(true); }}>
                  <Box bg="green.600" color="white" borderRadius="4px" w="36px" textAlign="center" py={0.5}
                    fontSize="10px" fontWeight="700" letterSpacing="0.03em">
                    <Text fontSize="8px" opacity={0.8}>£</Text><Text>IN</Text>
                  </Box>
                </Flex>
              </Th>}
              {v('inv_out') && <Th w={cw('inv_out')} textAlign="center" p={0} borderLeft={showTotals ? '1px solid' : 'none'} borderColor="gray.200" style={{ background:'#1a202c' }}>
                <Flex direction="column" align="center" lineHeight="1" cursor="pointer" onClick={() => { setShowTotals(!showTotals); if (!showTotals) setShowTotalsModal(true); }}>
                  <Box bg="red.600" color="white" borderRadius="4px" w="36px" textAlign="center" py={0.5}
                    fontSize="10px" fontWeight="700" letterSpacing="0.03em">
                    <Text fontSize="8px" opacity={0.8}>£</Text><Text>OUT</Text>
                  </Box>
                </Flex>
              </Th>}
              {v('vat') && <Th w={showTotals ? cw('vat') : 0} textAlign="center" p={0}
                borderLeft={showTotals ? '1px solid' : 'none'} borderColor="gray.200"
                style={{ background:'#1a202c', overflow:'hidden', maxWidth: showTotals ? undefined : 0, padding: showTotals ? undefined : 0 }}>
                {showTotals && <Text fontSize="9px" fontWeight="700" color="yellow.300">VAT</Text>}
              </Th>}
              {v('bal') && <Th w={showTotals ? cw('bal') : 0} textAlign="center" p={0}
                borderLeft={showTotals ? '1px solid' : 'none'} borderColor="gray.200"
                style={{ background:'#1a202c', overflow: 'hidden', maxWidth: showTotals ? undefined : 0, padding: showTotals ? undefined : 0 }}>
                {showTotals && <Text fontSize="9px" fontWeight="700" color="white">BAL</Text>}
              </Th>}
              {v('delete') && <Th w={cw('delete')} style={{ background:'#1a202c' }}></Th>}
            </Tr>
          {showTotals && (() => {
              let tInPaid = 0, tInUnpaid = 0, tInQuoted = 0, tOutPaid = 0, tOutUnpaid = 0, tOutQuoted = 0;
              let tVatIn = 0, tVatOut = 0;
              const countedProjectIds = new Set();
              Object.values(grouped).flat().forEach(p => {
                if (countOnlyPrimary) {
                  if (p._display_group_id !== getPrimaryGroupId(p)) return;
                  if (countedProjectIds.has(p.id)) return;
                  countedProjectIds.add(p.id);
                }
                (p.tasks || []).forEach(t => {
                  const ip = parseFloat(t.inv_in_paid || 0), iq = parseFloat(t.inv_in_quoted || 0);
                  const op = parseFloat(t.inv_out_paid || 0), oq = parseFloat(t.inv_out_quoted || 0);
                  tInPaid += ip; tInQuoted += iq; tInUnpaid += parseFloat(t.inv_in_total || 0) - ip - iq;
                  tOutPaid += op; tOutQuoted += oq; tOutUnpaid += parseFloat(t.inv_out_total || 0) - op - oq;
                  tVatIn  += parseFloat(t.inv_in_vat  || 0);
                  tVatOut += parseFloat(t.inv_out_vat || 0);
                });
              });
              const totalIn = tInPaid + tInUnpaid + tInQuoted;
              const totalOut = tOutPaid + tOutUnpaid + tOutQuoted;
              const vatNet = tVatIn - tVatOut; // positive = liability, negative = claim
              const totalBal = totalIn - totalOut - vatNet;
              const fmt = (v) => '£' + v.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
              return (
                <Tr style={{ backgroundColor: highlightColor }}>
                  <Td colSpan={COLS - (v('inv_in')?1:0) - (v('inv_out')?1:0) - (v('vat')?1:0) - (v('bal')?1:0) - (v('delete')?1:0)} p={1} textAlign="right" style={{ backgroundColor: highlightColor, border: 'none' }}>
                    <Text fontSize="10px" fontWeight="700" color="white" textTransform="uppercase" letterSpacing="0.05em">Totals in view</Text>
                  </Td>
                  <Td textAlign="center" p={1} style={{ backgroundColor: highlightColor, border: 'none', borderLeft: '1px solid rgba(255,255,255,0.3)' }}>
                    <Text fontSize="11px" fontWeight="800" color="white">{fmt(totalIn)}</Text>
                  </Td>
                  <Td textAlign="center" p={1} style={{ backgroundColor: highlightColor, border: 'none', borderLeft: '1px solid rgba(255,255,255,0.3)' }}>
                    <Text fontSize="11px" fontWeight="800" color="white">{fmt(totalOut)}</Text>
                  </Td>
                  {v('vat') && (
                    <Td textAlign="center" p={1} style={{ backgroundColor: highlightColor, border: 'none', borderLeft: '1px solid rgba(255,255,255,0.3)', overflow:'hidden', maxWidth: showTotals ? undefined : 0, padding: showTotals ? undefined : 0 }}>
                      {showTotals && <Text fontSize="11px" fontWeight="800" color={vatNet > 0 ? '#FC8181' : vatNet < 0 ? '#68D391' : 'white'}>
                        {vatNet > 0 ? '' : vatNet < 0 ? '+' : ''}{fmt(Math.abs(vatNet))}
                      </Text>}
                    </Td>
                  )}
                  <Td textAlign="center" p={1} style={{ backgroundColor: highlightColor, border: 'none', borderLeft: '1px solid rgba(255,255,255,0.3)', overflow:'hidden', maxWidth: showTotals ? undefined : 0, padding: showTotals ? undefined : 0 }}>
                    {showTotals && <Text fontSize="11px" fontWeight="800" color="white">
                      {totalBal < 0 ? '-' : ''}{fmt(Math.abs(totalBal))}
                    </Text>}
                  </Td>
                  {v('delete') && <Td style={{ backgroundColor: highlightColor, border: 'none' }} />}
                </Tr>
              );
            })()}


            {/* ── Finance filter action row — shown when totals active ── */}
            {showTotals && (filterInStatuses.length > 0 || filterOutStatuses.length > 0) && (
              <Tr bg="green.50" borderTop="1px solid" borderColor="green.100">
                <Td colSpan={COLS} px={3} py={1}>
                  <Flex align="center" gap={2}>
                    <Button size="xs" variant="ghost" colorScheme="gray" fontSize="9px"
                      onClick={() => { setFilterInStatuses([]); setFilterOutStatuses([]); }}>
                      Clear all
                    </Button>
                    <Button size="xs" variant="ghost" colorScheme="blue" fontSize="9px"
                      onClick={() => setShowTotalsModal(true)}>
                      Edit filters
                    </Button>
                    {onOpenBookkeeper && (
                      <Button size="xs" variant="ghost" colorScheme="blue" fontSize="9px" fontWeight="700"
                        onClick={() => onOpenBookkeeper(
                          filterInStatuses.length > 0 && filterOutStatuses.length === 0 ? 'out' :
                          filterOutStatuses.length > 0 && filterInStatuses.length === 0 ? 'in' : ''
                        )}>
                        Open in Bookkeeper →
                      </Button>
                    )}
                  </Flex>
                </Td>
              </Tr>
            )}
          </Thead>
          <Tbody>
            {rowGroups.map(group => {
              const groupProjects = grouped[group.id] || [];
              if (group.visibility === 'hide_if_empty' && groupProjects.length === 0) return null;
              if (showTotals && hideNoFinancialGroups) {
                const groupHasFinancials = groupProjects.some(p =>
                  (p.tasks || []).some(t =>
                    parseFloat(t.inv_in_total || 0) > 0 || parseFloat(t.inv_out_total || 0) > 0
                    || (t.inv_in_count || 0) > 0 || (t.inv_out_count || 0) > 0
                  )
                );
                if (!groupHasFinancials) return null;
              }
              return (
                <React.Fragment key={group.id}>
                  {(() => {
                    const gt = showTotals ? calcGroupTotals(groupProjects) : null;
                    const groupColor = group.color || '#2d3748';
                    const vatNet = gt ? gt.vatNet : 0;
                    const bal = gt ? gt.totalIn - gt.totalOut - vatNet : 0;
                    const extraCols = (v('inv_in')?1:0) + (v('inv_out')?1:0) + (v('vat')?1:0) + (v('bal')?1:0) + (v('delete')?1:0);
                    const swooshSpan = COLS - extraCols;
                    const swooshBg = { backgroundColor: groupColor };
                    const finCellStyle = showTotals
                      ? { ...swooshBg, padding: '2px 2px', verticalAlign: 'middle', border: 'none', whiteSpace: 'nowrap', textAlign: 'center' }
                      : { backgroundColor: 'transparent', padding: 0, border: 'none' };
                    return (
                      <Tr style={{ height: '28px', minHeight: '28px' }}>
                        {/* Main swoosh cell with group name */}
                        <Td colSpan={swooshSpan} p={0} border="none" style={{ padding: 0, background: 'transparent', height: '28px' }}>
                          <div style={{ position: 'relative', height: '28px', minHeight: '28px', overflow: 'visible' }}>
                            <svg width="100%" height="28" preserveAspectRatio="none" viewBox="0 0 1000 28"
                              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '28px', display: 'block' }}>
                              <path d="M0,0 L1000,0 L1000,4 L600,4 C500,4 400,28 300,28 L0,28 Z" fill={groupColor} />
                            </svg>
                            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                              display: 'flex', alignItems: 'center', padding: '0 4px', zIndex: 1 }}>
                              <Menu isLazy>
                                <MenuButton as={IconButton} icon={<FiPlus />} size="xs" variant="ghost" color="whiteAlpha.800"
                                  _hover={{ bg: 'whiteAlpha.300', color: 'white' }} aria-label="Add row" minW="20px" h="20px" />
                                <MenuList minW="140px" fontSize="12px">
                                  <MenuItem onClick={() => onAddProject(group.id)}>Empty Row</MenuItem>
                                  <MenuItem icon={<FiCopy />} onClick={() => { setTemplatePickerGroup(group.id); templatePickerDisclosure.onOpen(); }}>Use Template</MenuItem>
                                </MenuList>
                              </Menu>
                              <span style={{ color: 'white', fontWeight: 600, fontSize: '12px', letterSpacing: '0.02em', marginLeft: '4px' }}>
                                {group.name}
                              </span>
                            </div>
                          </div>
                        </Td>
                        {/* Per-column totals — cells always rendered to keep columns aligned */}
                        {v('inv_in') && (
                          <Td style={{ ...finCellStyle, borderLeft: showTotals ? '1px solid rgba(255,255,255,0.2)' : 'none', textAlign: 'center' }}>
                            {showTotals && gt ? (
                              <div>
                                <div style={{ fontSize: '7px', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.04em' }}>£ IN</div>
                                <div style={{ fontSize: '10px', fontWeight: 800, color: '#ffffff', whiteSpace: 'nowrap' }}>{fmtMny(gt.totalIn)}</div>
                              </div>
                            ) : null}
                          </Td>
                        )}
                        {v('inv_out') && (
                          <Td style={{ ...finCellStyle, borderLeft: showTotals ? '1px solid rgba(255,255,255,0.2)' : 'none', textAlign: 'center' }}>
                            {showTotals && gt ? (
                              <div>
                                <div style={{ fontSize: '7px', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.04em' }}>£ OUT</div>
                                <div style={{ fontSize: '10px', fontWeight: 800, color: '#ffffff', whiteSpace: 'nowrap' }}>{fmtMny(gt.totalOut)}</div>
                              </div>
                            ) : null}
                          </Td>
                        )}
                        {v('vat') && (
                          <Td style={{ ...finCellStyle, borderLeft: showTotals ? '1px solid rgba(255,255,255,0.2)' : 'none',
                            textAlign: 'center', width: showTotals ? undefined : 0, maxWidth: showTotals ? undefined : 0,
                            overflow: 'hidden', padding: showTotals ? '2px 2px' : 0 }}>
                            {showTotals && gt ? (
                              <div>
                                <div style={{ fontSize: '7px', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.04em' }}>VAT</div>
                                <div style={{ fontSize: '10px', fontWeight: 800,
                                  color: vatNet > 0 ? '#FC8181' : vatNet < 0 ? '#68D391' : '#ffffff' }}>
                                  {vatNet > 0 ? '' : vatNet < 0 ? '+' : ''}{fmtMny(Math.abs(vatNet))}
                                </div>
                              </div>
                            ) : null}
                          </Td>
                        )}
                        {v('bal') && (
                          <Td style={{ ...finCellStyle, borderLeft: showTotals ? '1px solid rgba(255,255,255,0.2)' : 'none',
                            textAlign: 'center',
                            width: showTotals ? undefined : 0,
                            maxWidth: showTotals ? undefined : 0,
                            overflow: 'hidden',
                            padding: showTotals ? '2px 2px' : 0 }}>
                            {showTotals && gt ? (
                              <div>
                                <div style={{ fontSize: '7px', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.04em' }}>BAL</div>
                                <div style={{ fontSize: '10px', fontWeight: 800,
                                  color: bal > 0 ? '#68D391' : bal < 0 ? '#FC8181' : '#ffffff' }}>
                                  {bal < 0 ? '-' : ''}{fmtMny(Math.abs(bal))}
                                </div>
                              </div>
                            ) : null}
                          </Td>
                        )}
                        {v('delete') && <Td style={{ backgroundColor: 'transparent', border: 'none' }} />}
                      </Tr>
                    );
                  })()}
                  {(() => {
                    let groupRowIdx = 0;
                    return groupProjects.map(project => {
                    const tasks = project.tasks || [];
                    if (tasks.length === 0) {
                      // Legacy: project with no tasks — apply same filters
                      if (hideDone && project.is_done) return null;
                      if (hideHeld && project.is_held) return null;
                      const altBg = groupRowIdx % 2 === 0 ? rowColor1 : rowColor2;
                      groupRowIdx++;
                      // Legacy: project with no tasks — show as single row with project info
                      return (
                        <Tr key={`p-${project.id}-${group.id}`}
                          data-project-id={project.id}
                          bg={dragOverProjectId === project.id ? 'blue.50' : altBg}
                          sx={dataHover}
                          onDragOver={(e) => handleDragOver(e, project.id, group.id)}
                          onDrop={(e) => handleDrop(e, project.id, group.id)}
                          borderTop={dragOverProjectId === project.id ? '2px solid' : undefined}
                          borderTopColor={dragOverProjectId === project.id ? 'blue.400' : undefined}
                          opacity={dragProjectId === project.id ? 0.4 : 1}>
                          {v('drag') && <Td className="data-cell" px={0} textAlign="center">
                            <Flex direction="column" align="center" gap={0}>
                              <Box cursor="grab" draggable onDragStart={(e) => handleDragStart(e, project.id, group.id)}
                                onDragEnd={handleDragEnd} color="gray.300" _hover={{ color: 'gray.600' }} mb={-1}>
                                <FiMenu size={12} />
                              </Box>
                              <Menu isLazy>
                                <MenuButton as={Box} cursor="pointer" color="gray.300" _hover={{ color: 'gray.600' }}
                                  fontSize="10px" lineHeight="1" mt={-0.5}>▾</MenuButton>
                                <MenuList fontSize="12px" maxH="300px" overflowY="auto" zIndex={200}>
                                  <MenuItem icon={<FiCopy />} onClick={() => handleCloneProject(project.id)}>Clone Project</MenuItem>
                                  {(allRowGroups || rowGroups).filter(g => g.id !== project._display_group_id).map(g => (
                                    <MenuItem key={g.id} onClick={() => handleMove(project.id, project._display_group_id, g.id)}>Move to {g.name}</MenuItem>
                                  ))}
                                </MenuList>
                              </Menu>
                            </Flex>
                          </Td>}
                          {v('done') && <Td className="data-cell" textAlign="center"><Checkbox size="sm" isChecked={!!project.is_done} colorScheme="green"
                            onChange={(e) => onUpdateProject(project.id, { is_done: e.target.checked })} /></Td>}
                          {v('wc') && <Td className="data-cell" textAlign="center" px={0}>
                            <WorkCentreSelect value={project.work_centre_name || ''} workCentreId={project.work_centre_id}
                              onSelect={(id) => onUpdateProject(project.id, { work_centre_id: id })} /></Td>}
                          {v('project') && <Td className="data-cell" onContextMenu={(e) => handleContextMenu(e, project)}>
                            <ProjectSearch value={project.project_name || ''} onSave={(val) => onUpdateProject(project.id, { project_name: val })} /></Td>}
                          {v('project_date') && <Td className="data-cell">
                            <InlineEdit value={project.commissioning_date ? project.commissioning_date.split('T')[0] : ''}
                              type="date" onSave={(val) => onUpdateProject(project.id, { commissioning_date: val || null })} /></Td>}
                          {v('deadline') && <Td className="data-cell">
                            <DeadlineCell projectId={project.id} deadlineDate={project.deadline_date}
                              deadlineType={project.deadline_type} onRefresh={onRefresh} /></Td>}
                          {v('client') && <Td className="data-cell" onContextMenu={(e) => {
                            const cos = project.companies || [];
                            if (cos.length === 1) handleEntityContext(e, 'company', cos[0], project.id);
                            else if (cos.length === 0) { e.preventDefault(); openAddEntity('company', project.id); }
                          }}>
                            {(project.companies || []).length === 0 ? (
                              <Text fontSize="11px" color="gray.400" cursor="pointer" onClick={() => openAddEntity('company', project.id)}>+ Company</Text>
                            ) : (
                              <Flex gap={0.5} flexWrap="wrap" px={1} py={0.5} align="center">
                                {(project.companies || []).map(c => (
                                  showLogos && c.logo_url ? (
                                    <Tooltip key={c.id} label={c.name} fontSize="xs">
                                      <Image src={`/api/files/${c.logo_url}`} alt={c.name} h={`${logoSize}px`} maxW={`${Math.round(logoSize * 2.5)}px`} objectFit="contain"
                                        cursor="pointer"
                                        onClick={(e) => { e.stopPropagation(); setQuickViewCompany(c.id); }}
                                        onContextMenu={(e) => handleEntityContext(e, 'company', c, project.id)}
                                        fallback={<Badge fontSize="9px" colorScheme="blue" variant="subtle"
                                          cursor="pointer"
                                          onClick={(e) => { e.stopPropagation(); setQuickViewCompany(c.id); }}>{c.name}</Badge>} />
                                    </Tooltip>
                                  ) : (
                                    <Badge key={c.id} fontSize="9px" colorScheme="blue" variant="subtle" cursor="pointer"
                                      onClick={(e) => { e.stopPropagation(); setQuickViewCompany(c.id); }}
                                      onContextMenu={(e) => handleEntityContext(e, 'company', c, project.id)}>{c.name}</Badge>
                                  )
                                ))}
                              </Flex>
                            )}
                          </Td>}
                          {v('contact') && <Td className="data-cell" onContextMenu={(e) => {
                            const cts = project.contacts || [];
                            if (cts.length === 1) handleEntityContext(e, 'contact', cts[0], project.id);
                            else if (cts.length === 0) { e.preventDefault(); openAddEntity('contact', project.id); }
                          }}>
                            {(project.contacts || []).length === 0 ? (
                              <Text fontSize="11px" color="gray.400" cursor="pointer" onClick={() => openAddEntity('contact', project.id)}>+ Contact</Text>
                            ) : (
                              <Flex gap={0.5} flexWrap="wrap" px={1} py={0.5} align="center">
                                {(project.contacts || []).map(c => (
                                  showAvatars && c.avatar_url ? (
                                    <Tooltip key={c.id} label={`${c.first_name} ${c.last_name || ''}`} fontSize="xs">
                                      <Image src={`/api/files/${c.avatar_url}`} alt={c.first_name} w={`${logoSize}px`} h={`${logoSize}px`} borderRadius="full" objectFit="cover"
                                        cursor="pointer"
                                        onClick={(e) => { e.stopPropagation(); setQuickViewContact(c.id); }}
                                        onContextMenu={(e) => handleEntityContext(e, 'contact', c, project.id)}
                                        fallback={<Badge fontSize="9px" colorScheme="purple" variant="subtle"
                                          cursor="pointer"
                                          onClick={(e) => { e.stopPropagation(); setQuickViewContact(c.id); }}>{c.first_name} {c.last_name || ''}</Badge>} />
                                    </Tooltip>
                                  ) : (
                                    <Badge key={c.id} fontSize="9px" colorScheme="purple" variant="subtle" cursor="pointer"
                                      onClick={(e) => { e.stopPropagation(); setQuickViewContact(c.id); }}
                                      onContextMenu={(e) => handleEntityContext(e, 'contact', c, project.id)}>{c.first_name} {c.last_name || ''}</Badge>
                                  )
                                ))}
                              </Flex>
                            )}
                          </Td>}
                          {v('action_type') && <Td className="data-cell"><ActionTypeSelect projectId={project.id} /></Td>}
                          {v('task') && <Td className="data-cell">
                            <Button size="xs" variant="outline" colorScheme="teal" h="20px" fontSize="10px" leftIcon={<FiPlus size={10} />} onClick={() => handleAddTask(project.id)}>Add task</Button>
                          </Td>}
                          {v('held') && <Td className="data-cell" textAlign="center"><Checkbox size="sm" isChecked={!!project.is_held} colorScheme="red"
                            onChange={(e) => onUpdateProject(project.id, { is_held: e.target.checked })} /></Td>}
                          {v('complete') && <Td className="data-cell"></Td>}
                          <Td colSpan={COLS - ['drag','done','wc','project','project_date','deadline','client','contact','action_type','task','held','complete'].filter(k => v(k)).length} textAlign="center">
                            {!v('task') && <Text fontSize="10px" color="gray.400" cursor="pointer" onClick={() => handleAddTask(project.id)}>+ Add task</Text>}
                          </Td>
                        </Tr>
                      );
                    }
                    let rowCounter = groupRowIdx;
                    // Filter tasks against the same predicates renderTaskRow
                    // uses so we know up-front which task will end up as the
                    // first visible row. Without this, `isFirst` was hard-
                    // coded to tasks[0]; hiding tasks[0] (e.g. via "Hide done"
                    // after ticking the first task complete) left the rest
                    // of the rows orphaned — project name / client / etc.
                    // are only rendered on the row marked isFirst, so the
                    // project appeared to disappear even though tasks 2+
                    // were still visible.
                    const visibleTasks = tasks.filter(t => {
                      if (hideHeld && t.is_held) return false;
                      if (hideDone && (project.is_done || t.is_complete)) return false;
                      if (showTotals && hideNoInvoice) {
                        const hasInvoice = parseFloat(t.inv_in_total || 0) > 0 || parseFloat(t.inv_out_total || 0) > 0
                          || (t.inv_in_count || 0) > 0 || (t.inv_out_count || 0) > 0;
                        if (!hasInvoice) return false;
                      }
                      if (showTotals && (invDateFrom || invDateTo) && !taskPassesDateFilter(t)) return false;
                      return true;
                    });
                    const firstVisibleTaskId = visibleTasks[0]?.id;
                    return tasks.map((task) => {
                      const row = renderTaskRow(project, task, task.id === firstVisibleTaskId, group, rowCounter);
                      if (row) rowCounter++;
                      return row;
                    }).concat((() => { groupRowIdx = rowCounter; return []; })());
                  });
                  })()}
                  {groupProjects.length === 0 && (
                    <Tr><Td colSpan={COLS} textAlign="center" color="gray.400" py={2} fontSize="11px">No items — click + to add</Td></Tr>
                  )}

                  {/* ── Group subtotal row ── */}
                  {showTotals && groupProjects.length > 0 && (() => {
                    const gt = calcGroupTotals(groupProjects);
                    if (gt.totalIn === 0 && gt.totalOut === 0) return null;
                    const vatNet = gt.vatNet || 0;
                    const bal = gt.totalIn - gt.totalOut - vatNet;
                    const gc = group.color || '#2d3748';
                    return (
                      <Tr>
                        <Td colSpan={COLS - (v('inv_in')?1:0) - (v('inv_out')?1:0) - (v('vat')?1:0) - (v('bal')?1:0) - (v('delete')?1:0)} p={1} textAlign="right"
                          borderBottom="2px solid" borderColor={gc} borderTop="1px solid" borderTopColor="gray.100">
                          <Text fontSize="9px" fontWeight="700" color="gray.400" textTransform="uppercase"
                            letterSpacing="wide">
                            {group.name} subtotal
                          </Text>
                        </Td>
                        {v('inv_in') && (
                          <Td textAlign="center" p={1} borderLeft="2px solid" borderColor={gc}
                            borderBottom="2px solid" borderTop="1px solid" borderTopColor="gray.100" style={{ background:'white' }}>
                            <Flex direction="column" lineHeight="1.2">
                              {gt.tInPaid > 0 && <Text fontSize="9px" fontWeight="800" color="green.700">{fmtMny(gt.tInPaid)}</Text>}
                              {gt.tInUnpaid > 0 && <Text fontSize="9px" fontWeight="700" color="gray.500">{fmtMny(gt.tInUnpaid)}</Text>}
                              {gt.tInQuoted > 0 && <Text fontSize="9px" fontWeight="700" color="blue.700">{fmtMny(gt.tInQuoted)}</Text>}
                              {gt.totalIn === 0 && <Text fontSize="9px" color="gray.300">—</Text>}
                            </Flex>
                          </Td>
                        )}
                        {v('inv_out') && (
                          <Td textAlign="center" p={1} borderLeft="1px solid" borderColor="gray.200"
                            borderBottom="2px solid" borderColor2={gc} borderTop="1px solid" borderTopColor="gray.100" style={{ background:'white' }}>
                            <Flex direction="column" lineHeight="1.2">
                              {gt.tOutPaid > 0 && <Text fontSize="9px" fontWeight="700" color="gray.400">{fmtMny(gt.tOutPaid)}</Text>}
                              {gt.tOutUnpaid > 0 && <Text fontSize="9px" fontWeight="800" color="red.600">{fmtMny(gt.tOutUnpaid)}</Text>}
                              {gt.tOutQuoted > 0 && <Text fontSize="9px" fontWeight="700" color="blue.700">{fmtMny(gt.tOutQuoted)}</Text>}
                              {gt.totalOut === 0 && <Text fontSize="9px" color="gray.300">—</Text>}
                            </Flex>
                          </Td>
                        )}
                        {v('vat') && (
                          <Td textAlign="center" p={1} borderLeft="1px solid" borderColor="gray.200"
                            borderBottom="2px solid" borderTop="1px solid" borderTopColor="gray.100" style={{ background:'white' }}>
                            <Text fontSize="10px" fontWeight="800"
                              color={vatNet > 0 ? 'red.600' : vatNet < 0 ? 'green.600' : 'gray.300'}>
                              {vatNet !== 0 ? `${vatNet < 0 ? '+' : ''}${fmtMny(Math.abs(vatNet))}` : '—'}
                            </Text>
                          </Td>
                        )}
                        {v('bal') && (
                          <Td textAlign="center" p={1} borderLeft="1px solid" borderColor="gray.200"
                            borderBottom="2px solid" borderTop="1px solid" borderTopColor="gray.100" style={{ background:'white' }}>
                            <Text fontSize="10px" fontWeight="800"
                              color={bal > 0 ? 'green.700' : bal < 0 ? 'red.700' : 'gray.400'}>
                              {bal !== 0 && (bal < 0 ? '-' : '')}{fmtMny(Math.abs(bal))}
                            </Text>
                          </Td>
                        )}
                        {v('delete') && <Td w={cw('delete')} borderBottom="2px solid" borderColor={gc} borderTop="1px solid" borderTopColor="gray.100" style={{ background:'white' }} />}
                      </Tr>
                    );
                  })()}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Context menu */}
      {contextMenu && (
        <Box ref={projectMenuRef}
          position="fixed" left={projectMenuPos.left} top={projectMenuPos.top} bg="white" border="1px"
          borderColor="gray.200" borderRadius="md" boxShadow="lg" zIndex={9999} py={1} minW="180px"
          maxH={projectMenuPos.maxHeight} maxW={projectMenuPos.maxWidth} overflowY="auto" overflowX="hidden"
          visibility={projectMenuPos.ready ? 'visible' : 'hidden'}>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'orange.50' }} color="orange.600" fontWeight="700"
            onClick={() => { if (onFocusProject) onFocusProject(contextMenu.project.id); setContextMenu(null); }}>
            🎯 Focus on this Project
          </Box>
          <Box h="1px" bg="gray.100" my={1} />
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => { openStatus(contextMenu.project); setContextMenu(null); }}>
            Add to another Row Group
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => { setDetailProjectId(contextMenu.project.id); detailDisclosure.onOpen(); setContextMenu(null); }}>
            View / Edit Project
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => { handleAddTask(contextMenu.project.id); setContextMenu(null); }}>
            Add a new task
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => {
              const pid = contextMenu.project.id;
              const on = shownHiddenProjectRef.current === pid;
              shownHiddenProjectRef.current = on ? null : pid;
              window.dispatchEvent(new CustomEvent('forgebos:show-hidden-tasks', { detail: { projectId: on ? null : pid } }));
              setContextMenu(null);
            }}>
            {shownHiddenProjectRef.current === contextMenu.project.id ? 'Hide Deleted & Completed Tasks' : 'Show Deleted & Completed Tasks'}
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'teal.50' }} color="teal.600"
            onClick={() => {
              setAssignUserTarget({ type: 'project', id: contextMenu.project.id, name: contextMenu.project.project_name || 'Project' });
              assignUserDisclosure.onOpen(); setContextMenu(null);
            }}>
            Assign User
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => { setPriorityProject(contextMenu.project); setContextMenu(null); }}>
            Set Priority Level
            {contextMenu.project.priority_level !== undefined && contextMenu.project.priority_level !== null &&
              <Text as="span" color="gray.400" ml={1}>({contextMenu.project.priority_level})</Text>}
          </Box>
          <Box h="1px" bg="gray.100" my={1} />
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }} fontWeight="600" color="blue.600"
            onClick={() => { setGanttProjectId(contextMenu.project.id); ganttDisclosure.onOpen(); setContextMenu(null); }}>
            View Project Gantt Chart
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }} color="blue.500"
            onClick={() => { handleCloneProject(contextMenu.project.id); setContextMenu(null); }}>
            Clone Project
          </Box>
          <Box h="1px" bg="gray.100" my={1} />
          {contextMenu.project.deleted_at ? (
            <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'green.50' }} color="green.600" fontWeight="600"
              onClick={() => { handleRestoreProject(contextMenu.project.id); setContextMenu(null); }}>
              Restore Project
            </Box>
          ) : (
            <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'red.50' }} color="red.500"
              onClick={() => { setDeleteConfirm(contextMenu.project); setDeleteReason(''); setContextMenu(null); }}>
              Delete Project
            </Box>
          )}
        </Box>
      )}

      {/* Task right-click menu */}
      {taskMenu && (
        <Box ref={taskMenuRef}
          position="fixed" left={taskMenuPos.left} top={taskMenuPos.top} bg="white" border="1px"
          borderColor="gray.200" borderRadius="md" boxShadow="lg" zIndex={9999} py={1} minW="220px"
          maxH={taskMenuPos.maxHeight} maxW={taskMenuPos.maxWidth} overflowY="auto" overflowX="hidden"
          visibility={taskMenuPos.ready ? 'visible' : 'hidden'}>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => { handleAddTask(taskMenu.project.id); setTaskMenu(null); }}>
            Add Task
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => { openTask(taskMenu.project, taskMenu.task); setTaskMenu(null); }}>
            Edit Task
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={async () => {
              const t = taskMenu.task; setTaskMenu(null);
              try {
                const r = await api.post('/invoice-intake/portals/task', { task_id: t.id });
                await navigator.clipboard?.writeText(r.data.url);
                toast({ title: 'Invoice upload link copied', description: `Anyone with it can upload receipts/invoices to "${t.task_title}" — nothing else.`, status: 'success', duration: 4000 });
              } catch (e) { toast({ title: 'Could not create the link', description: e?.response?.data?.error, status: 'error', duration: 3000 }); }
            }}>
            Copy invoice upload link
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => {
              setWorkSessionTask({ taskId: taskMenu.task.id, taskTitle: taskMenu.task.task_title, projectId: taskMenu.project.id });
              workSessionDisclosure.onOpen(); setTaskMenu(null);
            }}>
            Clock On — Work Session
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => {
              setProgressTaskId(taskMenu.task.id); setProgressTaskTitle(taskMenu.task.task_title);
              progressDisclosure.onOpen(); setTaskMenu(null);
            }}>
            Progress Updates
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => { openCalendar(taskMenu.project.id, taskMenu.task.id, taskMenu.project.tasks); setTaskMenu(null); }}>
            Set Deadline (Calendar)
          </Box>
          <Box h="1px" bg="gray.100" my={0.5} />
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }} color="blue.600" fontWeight="600"
            onClick={() => { setCloneTask(taskMenu.task); taskCloneDisclosure.onOpen(); setTaskMenu(null); }}>
            Clone Task
          </Box>
          {!taskMenu.task.deleted_at && (
            <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'red.50' }} color="red.600" fontWeight="600"
              onClick={() => { setDeleteTaskTarget(taskMenu.task); setTaskMenu(null); }}>
              Delete Task
            </Box>
          )}
          {!!taskMenu.task.deleted_at && (
            <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'green.50' }} color="green.600" fontWeight="600"
              onClick={async () => {
                const t = taskMenu.task; setTaskMenu(null);
                // SITAC restoration: deletion evidence is preserved in the
                // chain; the task (and its cascade-deleted sub-tasks)
                // revive under the current user.
                try { await api.post(`/tasks/${t.id}/restore`); onRefresh(); } catch (e) {}
              }}>
              Un-delete Task
            </Box>
          )}
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'teal.50' }} color="teal.600"
            onClick={() => {
              setAssignUserTarget({ type: 'task', id: taskMenu.task.id, name: taskMenu.task.task_title || 'Task' });
              assignUserDisclosure.onOpen(); setTaskMenu(null);
            }}>
            Assign User
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'purple.50' }} color="purple.600"
            onClick={() => {
              setAddContactTarget({ type: 'task', id: taskMenu.task.id, name: taskMenu.task.task_title || 'Task' });
              addContactDisclosure.onOpen(); setTaskMenu(null);
            }}>
            Add Contact
          </Box>
          <Box h="1px" bg="gray.100" my={0.5} />
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'green.50' }} color="green.700"
            onClick={async () => {
              // Primary action: toggle a row in the NEW todo_items table for
              // the current user. This is what makes the task appear on
              // /todo. The old `tasks.appear_in_todo` flag is also flipped
              // for backward compatibility with the legacy ToDoListsDashboard
              // and anything else still keying off it — but the user-facing
              // truth lives in todo_items now.
              try {
                const { data } = await api.post('/todo-items/toggle-link',
                  { task_id: taskMenu.task.id });
                // Legacy flag — best-effort, doesn't matter if it fails.
                api.put(`/tasks/${taskMenu.task.id}/todo-appear`,
                  { appear: !!data?.linked }).catch(() => {});
              } catch (e) {
                // Fall through; the menu will close and onRefresh will
                // re-fetch in case the optimistic state was wrong.
              }
              onRefresh(); setTaskMenu(null);
            }}>
            {taskMenuLinkedTodo === true  ? '✓ Remove from To Do List'
            : taskMenuLinkedTodo === false ? '+ Add to To Do List'
            :                                '… Add to To Do List'}
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'green.50' }} color="green.700"
            onClick={async () => {
              const { data } = await api.get(`/tasks/${taskMenu.task.id}/todo-users`).catch(() => ({ data: [] }));
              const { data: allU } = await api.get('/calendar/users').catch(() => ({ data: [] }));
              setTodoUsersAll(allU);
              setTodoUsersSelected(data.map(u => u.id));
              setTodoUsersTarget({ id: taskMenu.task.id, type: 'task', title: taskMenu.task.task_title });
              setTaskMenu(null);
            }}>
            👥 Manage To Do Users
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'orange.50' }} color="orange.600"
            onClick={() => {
              setTodoDeadlineTarget({ id: taskMenu.task.id, type: 'task', title: taskMenu.task.task_title });
              setTodoDeadlineForm({ date: taskMenu.task.todo_deadline_date?.split('T')[0] || '', time: taskMenu.task.todo_deadline_time?.slice(0,5) || '', deadlineType: taskMenu.task.todo_deadline_type || 'hard', leeway: taskMenu.task.todo_deadline_leeway_days || 1 });
              setTaskMenu(null);
            }}>
            📅 Set To Do Deadline
          </Box>
          {taskMenu.task.source_task_id && (
            <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'purple.50' }} color="purple.600"
              onClick={() => { setCloneTask(taskMenu.task); taskCloneHistoryDisclosure.onOpen(); setTaskMenu(null); }}>
              View Clone History
            </Box>
          )}
        </Box>
      )}

      {/* Entity right-click menu (company/contact) */}
      {entityMenu && (
        <Box ref={entityMenuRef}
          position="fixed" left={entityMenuPos.left} top={entityMenuPos.top} bg="white" border="1px"
          borderColor="gray.200" borderRadius="md" boxShadow="lg" zIndex={9999} py={1} minW="220px"
          maxH={entityMenuPos.maxHeight} maxW={entityMenuPos.maxWidth} overflowY="auto" overflowX="hidden"
          visibility={entityMenuPos.ready ? 'visible' : 'hidden'}>
          <Box px={3} py={1} fontSize="11px" color="gray.400" fontWeight="600">
            {entityMenu.type === 'company' ? entityMenu.entity.name : `${entityMenu.entity.first_name} ${entityMenu.entity.last_name || ''}`}
          </Box>
          <Box h="1px" bg="gray.100" />
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'red.50' }} color="red.500"
            onClick={handleEntityRemove}>
            Remove {entityMenu.type === 'company' ? 'company' : 'contact'} from {entityMenu.taskId ? 'task' : 'project'}
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => openAddEntity(entityMenu.type, entityMenu.projectId, entityMenu.taskId)}>
            Add another {entityMenu.type === 'company' ? 'company' : 'contact'}
          </Box>
          <Box px={3} py={1.5} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
            onClick={() => openAddEntity(entityMenu.type === 'company' ? 'contact' : 'company', entityMenu.projectId, entityMenu.taskId)}>
            Add a {entityMenu.type === 'company' ? 'contact' : 'company'}
          </Box>
        </Box>
      )}

      {/* Add entity search modal */}
      {addEntityModal && (
        <Modal isOpen={true} onClose={() => { setAddEntityModal(null); setShowCreateEntity(false); }} size="sm" isCentered>
          <ModalOverlay /><ModalContent>
            <ModalHeader fontSize="md">
              Add {addEntityModal.type === 'company' ? 'Company' : 'Contact'} to {addEntityModal.taskId ? 'Task' : 'Project'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={4}>
              <Input size="sm" placeholder={`Search ${addEntityModal.type === 'company' ? 'companies' : 'contacts'}...`}
                value={entitySearch} autoFocus
                onChange={(e) => { setEntitySearch(e.target.value); searchEntities(e.target.value); }} />
              {entityResults.length > 0 && (
                <Box border="1px" borderColor="gray.200" borderRadius="md" mt={2}
                  maxH="260px" overflowY="auto"
                  sx={{ '&::-webkit-scrollbar': { w:'4px' }, '&::-webkit-scrollbar-thumb': { bg:'gray.300', borderRadius:'2px' } }}>
                  {entityResults.map(r => (
                    <Box key={r.id} px={3} py={2} fontSize="12px" cursor="pointer" _hover={{ bg: 'blue.50' }}
                      borderBottom="1px" borderColor="gray.50"
                      onClick={() => handleEntityAdd(r)}>
                      <Text fontWeight="500">{addEntityModal.type === 'company' ? r.name : `${r.first_name} ${r.last_name || ''}`}</Text>
                      {addEntityModal.type !== 'company' && r.company_name && (
                        <Text fontSize="10px" color="gray.500">{r.company_name}</Text>
                      )}
                      {addEntityModal.type !== 'company' && r.job_title && (
                        <Text fontSize="10px" color="gray.400">{r.job_title}</Text>
                      )}
                    </Box>
                  ))}
                  {entityResults.length === 30 && (
                    <Box px={3} py={1.5} bg="gray.50" borderTop="1px" borderColor="gray.100">
                      <Text fontSize="10px" color="gray.400">Showing first 30 — type to filter</Text>
                    </Box>
                  )}
                </Box>
              )}
              {entitySearch.length > 0 && entityResults.length === 0 && (
                <Text fontSize="11px" color="gray.400" mt={2}>No results found</Text>
              )}
              <Button size="sm" variant="outline" colorScheme="blue" mt={3} w="100%"
                leftIcon={<FiPlus />}
                onClick={() => setShowCreateEntity(true)}>
                Create New {addEntityModal.type === 'company' ? 'Company' : 'Contact'}
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      {/* Create company/contact modals from entity search */}
      {showCreateEntity && addEntityModal?.type === 'company' && (
        <CompanyModal isOpen={true} onClose={() => setShowCreateEntity(false)}
          initialName={entitySearch}
          onCreated={async (company) => {
            setShowCreateEntity(false);
            if (company?.id) { await handleEntityAdd(company); }
          }} />
      )}
      {showCreateEntity && addEntityModal?.type === 'contact' && (
        <ContactModal isOpen={true} onClose={() => setShowCreateEntity(false)}
          companyId={addEntityModal.companies?.[0]?.id}
          onCreated={async (contact) => {
            setShowCreateEntity(false);
            if (contact?.id) { await handleEntityAdd(contact); }
          }} />
      )}
      {/* Priority Level Modal */}
      {priorityProject && (
        <Modal isOpen={true} onClose={() => setPriorityProject(null)} size="sm" isCentered>
          <ModalOverlay /><ModalContent>
            <ModalHeader fontSize="md">Set Priority Level</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={4}>
              <Text fontSize="12px" color="gray.500" mb={3}>
                {priorityProject.project_name || 'Untitled Project'}
              </Text>
              <Flex justify="space-between" mb={1}>
                <Text fontSize="xs" fontWeight="600">Priority</Text>
                <Badge colorScheme={priorityProject.priority_level >= 8 ? 'red' : priorityProject.priority_level >= 5 ? 'orange' : 'gray'}
                  fontSize="md" px={2}>{priorityProject.priority_level ?? 5}</Badge>
              </Flex>
              <Slider min={0} max={10} step={1} value={priorityProject.priority_level ?? 5}
                onChange={(v) => setPriorityProject(p => ({ ...p, priority_level: v }))}
                colorScheme={priorityProject.priority_level >= 8 ? 'red' : priorityProject.priority_level >= 5 ? 'orange' : 'blue'}>
                <SliderTrack h="8px" borderRadius="full">
                  <SliderFilledTrack />
                </SliderTrack>
                <SliderThumb boxSize={5} />
              </Slider>
              <Flex justify="space-between" mt={1}>
                <Text fontSize="9px" color="gray.400">0 (Lowest)</Text>
                <Text fontSize="9px" color="gray.400">5 (Normal)</Text>
                <Text fontSize="9px" color="gray.400">10 (Urgent)</Text>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button size="sm" variant="ghost" mr={2} onClick={() => setPriorityProject(null)}>Cancel</Button>
              <Button size="sm" bg="brand.600" color="white" _hover={{ bg: 'brand.700' }}
                onClick={async () => {
                  await onUpdateProject(priorityProject.id, { priority_level: priorityProject.priority_level ?? 5 });
                  setPriorityProject(null);
                }}>Save</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Procedures & Password Safe — mounted UNCONDITIONALLY. This must
          NOT live inside the selectedProjectId gate below: openProcSafe
          doesn't set selectedProjectId (the modal resolves its own project
          context server-side), so gating it there left the icon clicks
          setting state into a modal that wasn't rendered at all — the
          "click does nothing, no console output" bug. */}
      <ConfirmDialog isOpen={!!deleteTaskTarget} isLoading={deletingTask}
        onClose={() => setDeleteTaskTarget(null)}
        title="Delete Task"
        message={`Are you sure you want to delete task \u201C${deleteTaskTarget?.task_title || 'New Task'}\u201D? Deleted tasks can be recovered later if needed, and will stay here for ${configDeleteDays} day${configDeleteDays === 1 ? '' : 's'} when they will be hidden from view.`}
        confirmLabel="DELETE"
        onConfirm={async () => {
          setDeletingTask(true);
          try { await api.delete(`/tasks/${deleteTaskTarget.id}`); setDeleteTaskTarget(null); onRefresh(); }
          catch (e) {}
          finally { setDeletingTask(false); }
        }} />

      <TaskProcSafeModal isOpen={!!procSafeTarget} taskId={procSafeTarget?.taskId}
        mode={procSafeTarget?.tab}
        title={procSafeTarget?.tab === 'passwords' ? '🔐 Task Password Safe' : '📋 Task Procedures'}
        onClose={() => { setProcSafeTarget(null); onRefresh(); }} />

      {selectedProjectId && (
        <>
          <CommunicationsModal projectId={selectedProjectId} taskId={selectedTaskId} isOpen={commDisclosure.isOpen}
            onClose={() => { commDisclosure.onClose(); onRefresh(); }} />
          <DocumentsModal projectId={selectedProjectId} taskId={selectedTaskId} isOpen={docsDisclosure.isOpen}
            onClose={() => { docsDisclosure.onClose(); onRefresh(); }}
            onOpenComms={() => { docsDisclosure.onClose(); commDisclosure.onOpen(); }}
            onOpenNotes={() => { docsDisclosure.onClose(); notesDisclosure.onOpen(); }}
            onOpenInvoice={(dir) => {
              docsDisclosure.onClose();
              if (dir === 'in') invoiceInDisclosure.onOpen();
              else invoiceDisclosure.onOpen();
            }}
          />
          <TaskCloneModal task={cloneTask} isOpen={taskCloneDisclosure.isOpen}
            onClose={taskCloneDisclosure.onClose} onCloned={onRefresh} />
          <TaskCloneHistoryModal task={cloneTask} isOpen={taskCloneHistoryDisclosure.isOpen}
            onClose={taskCloneHistoryDisclosure.onClose} />
          <NotesModal projectId={selectedProjectId} taskId={selectedTaskId} isOpen={notesDisclosure.isOpen}
            onClose={() => { notesDisclosure.onClose(); onRefresh(); }} />
          <InvoiceModal projectId={selectedProjectId} taskId={selectedTaskId} isOpen={invoiceDisclosure.isOpen}
            onClose={() => { invoiceDisclosure.onClose(); onRefresh(); }} />
          <InvoiceModal projectId={selectedProjectId} taskId={selectedTaskId} direction="in" isOpen={invoiceInDisclosure.isOpen}
            onClose={() => { invoiceInDisclosure.onClose(); onRefresh(); }} />
          <CalendarModal projectId={selectedProjectId} taskId={selectedTaskId} gridTasks={selectedCalGridTasks} isOpen={calendarDisclosure.isOpen}
            onClose={() => { calendarDisclosure.onClose(); onRefresh(); }} />
          <ResourceBookingModal projectId={selectedProjectId} taskId={selectedTaskId} isOpen={bookingsDisclosure.isOpen}
            onClose={() => { bookingsDisclosure.onClose(); onRefresh(); }} />
          <PlannedVisitsTaskModal taskId={plannedVisitsTaskId} taskTitle={plannedVisitsTaskTitle}
            projectId={plannedVisitsProjectId}
            isOpen={plannedVisitsDisclosure.isOpen} onClose={plannedVisitsDisclosure.onClose} />
          <AppointmentModal isOpen={diaryDisclosure.isOpen} onClose={diaryDisclosure.onClose}
            onSaved={() => {}} prefill={diaryPrefill} currentUser={authUser} />
          {diaryContactPicker && (() => {
            const { project, task, contacts: dc, users: du = [] } = diaryContactPicker;
            const allCount = dc.length + du.length;
            const buildPrefill = (selContacts) => ({
              project_id: String(project.id), task_id: String(task.id),
              company_id: project.company_id || null,
              contact_ids: selContacts.map(c => c.id),
              prefill_contacts: selContacts,
            });
            return (
              <Modal isOpen={!!diaryContactPicker} onClose={() => setDiaryContactPicker(null)} size="sm">
                <ModalOverlay />
                <ModalContent>
                  <ModalHeader fontSize="md">Request an Appointment
                    <Text fontSize="11px" fontWeight="400" color="gray.400" mt={0.5}>{task.task_title}</Text>
                  </ModalHeader>
                  <ModalCloseButton />
                  <ModalBody pb={4}>
                    {dc.length > 0 && (
                      <Box mb={3}>
                        <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={2}>Contacts</Text>
                        <VStack spacing={1} align="stretch">
                          {dc.map(c => (
                            <Flex key={c.id} align="center" justify="space-between" p={2} border="1px" borderColor="gray.200" borderRadius="md">
                              <Box>
                                <Text fontSize="12px" fontWeight="600">{c.first_name} {c.last_name || ''}</Text>
                                {c.email && <Text fontSize="10px" color="gray.400">{c.email}</Text>}
                              </Box>
                              <Button size="xs" colorScheme="teal" onClick={() => { setDiaryPrefill(buildPrefill([c])); setDiaryContactPicker(null); diaryDisclosure.onOpen(); }}>Select</Button>
                            </Flex>
                          ))}
                        </VStack>
                      </Box>
                    )}
                    {du.length > 0 && (
                      <Box mb={3}>
                        <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" mb={2}>Team Members</Text>
                        <VStack spacing={1} align="stretch">
                          {du.map(u => {
                            const uid = u.id || u.user_id;
                            const name = u.display_name || u.username || '';
                            return (
                              <Flex key={uid} align="center" justify="space-between" p={2} border="1px" borderColor="blue.100" borderRadius="md" bg="blue.50">
                                <Text fontSize="12px" fontWeight="600">{name}</Text>
                                <Button size="xs" colorScheme="blue" onClick={() => {
                                  // Users go as resource/host, not contacts — just open with project prefill
                                  setDiaryPrefill({ project_id: String(project.id), task_id: String(task.id), company_id: project.company_id || null, contact_ids: [], prefill_contacts: [] });
                                  setDiaryContactPicker(null); diaryDisclosure.onOpen();
                                }}>Invite</Button>
                              </Flex>
                            );
                          })}
                        </VStack>
                      </Box>
                    )}
                    {allCount === 0 && <Text fontSize="12px" color="gray.400" mb={3}>No contacts or users assigned to this task/project.</Text>}
                    <Flex gap={2} mt={2}>
                      <Button size="sm" variant="ghost" flex={1} onClick={() => setDiaryContactPicker(null)}>Cancel</Button>
                      {allCount > 1 && (
                        <Button size="sm" colorScheme="purple" flex={1} onClick={() => {
                          setDiaryPrefill(buildPrefill(dc));
                          setDiaryContactPicker(null); diaryDisclosure.onOpen();
                        }}>Invite All Contacts ({dc.length})</Button>
                      )}
                    </Flex>
                  </ModalBody>
                </ModalContent>
              </Modal>
            );
          })()}
          <SuppliersModal projectId={selectedProjectId} taskId={selectedTaskId} gridTasks={selectedSupplierGridTasks} isOpen={suppliersDisclosure.isOpen}
            onClose={() => { suppliersDisclosure.onClose(); onRefresh(); }} onRefresh={onRefresh} />
          <MileageModal projectId={selectedProjectId} taskId={selectedTaskId} isOpen={mileageDisclosure.isOpen}
            onClose={() => { mileageDisclosure.onClose(); onRefresh(); }} />
          <FeedbackModal projectId={selectedProjectId} taskId={selectedTaskId}
            taskTitle={null}
            subTasks={[]} isOpen={feedbackDisclosure.isOpen}
            onClose={feedbackDisclosure.onClose} onRefresh={onRefresh} />
          <IncidentBlogPage taskId={selectedTaskId} taskTitle={null} projectId={selectedProjectId}
            isOpen={incidentDisclosure.isOpen} onClose={incidentDisclosure.onClose} onRefresh={onRefresh} />

          {/* ── Todo Deadline Modal ── */}
          {todoDeadlineTarget && (
            <Modal isOpen={!!todoDeadlineTarget} onClose={() => setTodoDeadlineTarget(null)} size="sm" isCentered>
              <ModalOverlay /><ModalContent>
                <ModalHeader fontSize="md">Set To Do Deadline</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={4}>
                  <Text fontSize="12px" color="gray.500" mb={3} noOfLines={2}>{todoDeadlineTarget.title}</Text>
                  <VStack spacing={3} align="stretch">
                    <Box>
                      <Text fontSize="11px" fontWeight="600" mb={1}>Date *</Text>
                      <Input size="sm" type="date" value={todoDeadlineForm.date} onChange={e => setTodoDeadlineForm(p=>({...p,date:e.target.value}))} />
                    </Box>
                    <Box>
                      <Text fontSize="11px" fontWeight="600" mb={1}>Time (optional)</Text>
                      <Input size="sm" type="time" value={todoDeadlineForm.time} onChange={e => setTodoDeadlineForm(p=>({...p,time:e.target.value}))} />
                    </Box>
                    <Box>
                      <Text fontSize="11px" fontWeight="600" mb={1}>Deadline Type</Text>
                      <Select size="sm" value={todoDeadlineForm.deadlineType} onChange={e => setTodoDeadlineForm(p=>({...p,deadlineType:e.target.value}))}>
                        <option value="hard">Hard Deadline — must be done by this date</option>
                        <option value="soft">Soft Deadline — allow leeway</option>
                      </Select>
                    </Box>
                    {todoDeadlineForm.deadlineType === 'soft' && (
                      <Box>
                        <Text fontSize="11px" fontWeight="600" mb={1}>Leeway (days)</Text>
                        <Select size="sm" value={todoDeadlineForm.leeway} onChange={e => setTodoDeadlineForm(p=>({...p,leeway:parseInt(e.target.value)}))}>
                          {[1,2,3,4,5,10,15,20,25,30,60,100].map(d=><option key={d} value={d}>{d} day{d>1?'s':''}</option>)}
                        </Select>
                      </Box>
                    )}
                  </VStack>
                  <HStack justify="flex-end" mt={4} spacing={2}>
                    <Button size="sm" variant="ghost" onClick={() => setTodoDeadlineTarget(null)}>Cancel</Button>
                    <Button size="sm" colorScheme="orange" onClick={async () => {
                      const ep = todoDeadlineTarget.type === 'task' ? `/tasks/${todoDeadlineTarget.id}/todo-deadline` : `/sub-tasks/${todoDeadlineTarget.id}/todo-deadline`;
                      await api.put(ep, { date: todoDeadlineForm.date, time: todoDeadlineForm.time || null, type: todoDeadlineForm.deadlineType, leeway_days: todoDeadlineForm.deadlineType==='soft' ? todoDeadlineForm.leeway : null });
                      onRefresh(); setTodoDeadlineTarget(null);
                    }}>Save Deadline</Button>
                  </HStack>
                </ModalBody>
              </ModalContent>
            </Modal>
          )}

          {/* ── Todo Users Modal ── */}
          {todoUsersTarget && (
            <Modal isOpen={!!todoUsersTarget} onClose={() => setTodoUsersTarget(null)} size="sm" isCentered>
              <ModalOverlay /><ModalContent>
                <ModalHeader fontSize="md">Manage To Do List Users</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={4}>
                  <Text fontSize="12px" color="gray.500" mb={3} noOfLines={2}>{todoUsersTarget.title}</Text>
                  <Text fontSize="11px" color="gray.400" mb={2}>Select users who should see this in their To Do list:</Text>
                  <VStack spacing={1} align="stretch" maxH="240px" overflowY="auto">
                    {todoUsersAll.map(u => {
                      const sel = todoUsersSelected.includes(u.id);
                      return (
                        <Flex key={u.id} align="center" gap={2} p={2} borderRadius="md" cursor="pointer"
                          bg={sel ? 'green.50' : 'white'} border="1px" borderColor={sel ? 'green.300' : 'gray.200'}
                          onClick={() => setTodoUsersSelected(prev => sel ? prev.filter(x => x !== u.id) : [...prev, u.id])}>
                          <Box w="14px" h="14px" border="2px" borderColor={sel?'green.400':'gray.300'} borderRadius="sm"
                            bg={sel?'green.400':'white'} display="flex" alignItems="center" justifyContent="center">
                            {sel && <Text fontSize="9px" color="white" fontWeight="900">✓</Text>}
                          </Box>
                          <Text fontSize="12px" fontWeight={sel?'700':'400'}>{u.display_name || u.username}</Text>
                        </Flex>
                      );
                    })}
                  </VStack>
                  <HStack justify="flex-end" mt={4} spacing={2}>
                    <Button size="sm" variant="ghost" onClick={() => setTodoUsersTarget(null)}>Cancel</Button>
                    <Button size="sm" colorScheme="green" isLoading={savingTodoUsers} onClick={async () => {
                      setSavingTodoUsers(true);
                      const ep = todoUsersTarget.type === 'task' ? `/tasks/${todoUsersTarget.id}/todo-users` : `/sub-tasks/${todoUsersTarget.id}/todo-users`;
                      await api.post(ep, { user_ids: todoUsersSelected.map(Number) }).catch(()=>{});
                      setSavingTodoUsers(false); setTodoUsersTarget(null); onRefresh();
                    }}>Save</Button>
                  </HStack>
                </ModalBody>
              </ModalContent>
            </Modal>
          )}
        </>
      )}
      <AssignUserModal
        entityType={assignUserTarget?.type}
        entityId={assignUserTarget?.id}
        entityName={assignUserTarget?.name}
        isOpen={assignUserDisclosure.isOpen}
        onClose={assignUserDisclosure.onClose}
        onSaved={onRefresh}
      />
      <AddContactModal
        entityType={addContactTarget?.type}
        entityId={addContactTarget?.id}
        entityName={addContactTarget?.name}
        isOpen={addContactDisclosure.isOpen}
        onClose={addContactDisclosure.onClose}
        onSaved={onRefresh}
      />
      <StatusModal projectId={statusProjectId} currentGroupIds={statusGroupIds}
        allGroups={allRowGroups || rowGroups} viewGroups={rowGroups}
        isOpen={statusDisclosure.isOpen} onClose={statusDisclosure.onClose} onRefresh={onRefresh} />
      <TaskEditModal isOpen={taskDisclosure.isOpen}
        onClose={() => { taskDisclosure.onClose(); onRefresh(); }}
        taskTitle={taskProject?._task?.task_title || ''}
        jobDescription={taskProject?._task?.description || ''}
        taskData={taskProject?._task ? { ...taskProject._task, company_id: taskProject.company_id } : null}
        onSave={handleTaskSave} />
      {holdTask && (
        <HoldModal taskId={holdTask.id} isHeld={!!holdTask.is_held}
          isOpen={holdDisclosure.isOpen}
          onClose={() => { holdDisclosure.onClose(); setHoldTask(null); }}
          onRefresh={onRefresh} />
      )}
      <ProspectsModal isOpen={prospectDisclosure.isOpen}
        onClose={() => { prospectDisclosure.onClose(); setProspectEditData(null); }}
        onRefresh={onRefresh} editData={prospectEditData} />
      <GanttModal projectId={ganttProjectId} isOpen={ganttDisclosure.isOpen}
        onClose={() => { ganttDisclosure.onClose(); setGanttProjectId(null); }} />
      <ProgressModal taskId={progressTaskId} taskTitle={progressTaskTitle}
        isOpen={progressDisclosure.isOpen}
        onClose={() => { progressDisclosure.onClose(); setProgressTaskId(null); onRefresh(); }}
        onRefresh={onRefresh} />
      <ProjectDetailModal projectId={detailProjectId} isOpen={detailDisclosure.isOpen}
        onClose={() => { detailDisclosure.onClose(); setDetailProjectId(null); }} onRefresh={onRefresh} />
      {workSessionTask && (
        <WorkSessionModal taskId={workSessionTask.taskId} taskTitle={workSessionTask.taskTitle}
          projectId={workSessionTask.projectId} isOpen={workSessionDisclosure.isOpen}
          onClose={() => { workSessionDisclosure.onClose(); setWorkSessionTask(null); }} onRefresh={onRefresh} />
      )}

      {/* Delete Project Confirmation */}
      {/* ── Delete Project — Step 1 ── */}
      {deleteConfirm && !deleteFinalConfirm && (
        <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} size="sm">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontSize="md" color="red.600">Delete Project</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text fontSize="13px" mb={3}>
                Are you sure you want to delete <strong>{deleteConfirm.project_name || 'this project'}</strong>
                {deleteConfirm.company_name ? <Text as="span" color="gray.500"> ({deleteConfirm.company_name})</Text> : ''}?
              </Text>
              <Text fontSize="11px" color="gray.500" mb={3}>
                The project and all its content (tasks, communications, notes, invoices) will be soft-deleted and can be restored by an admin.
              </Text>
              <Input size="sm" placeholder="Reason for deletion (optional)" value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)} mb={2} />
            </ModalBody>
            <ModalFooter>
              <Button size="sm" variant="ghost" mr={2} onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <Button size="sm" colorScheme="red" onClick={() => {
                setDeleteFinalConfirm(deleteConfirm);
                setDeleteConfirm(null);
              }}>Continue →</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* ── Delete Project — Step 2 (Final Confirm) ── */}
      {deleteFinalConfirm && (
        <Modal isOpen={!!deleteFinalConfirm} onClose={() => { setDeleteFinalConfirm(null); setDeleteReason(''); }} size="sm">
          <ModalOverlay />
          <ModalContent borderTop="4px solid" borderColor="red.500">
            <ModalHeader fontSize="md" color="red.700">⚠️ Final Confirmation</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box bg="red.50" border="1px" borderColor="red.200" borderRadius="md" p={3} mb={3}>
                <Text fontSize="13px" fontWeight="700" color="red.800">{deleteFinalConfirm.project_name || 'This project'}</Text>
                {deleteFinalConfirm.company_name && <Text fontSize="11px" color="red.600">{deleteFinalConfirm.company_name}</Text>}
              </Box>
              <Text fontSize="12px" color="gray.700" mb={2}>
                This project will be soft-deleted. It will be hidden from the dashboard but retained in the database and can be restored by an admin via right-click → Restore Project.
              </Text>
              {deleteReason && <Text fontSize="11px" color="gray.500" mb={2}>Reason: <em>{deleteReason}</em></Text>}
              <Text fontSize="12px" fontWeight="700" color="red.600">Are you absolutely sure?</Text>
            </ModalBody>
            <ModalFooter>
              <Button size="sm" variant="ghost" mr={2} onClick={() => { setDeleteFinalConfirm(null); setDeleteReason(''); }}>
                Cancel
              </Button>
              <Button size="sm" colorScheme="red" fontWeight="700" onClick={async () => {
                await onDeleteProject(deleteFinalConfirm.id, deleteReason || null);
                setDeleteFinalConfirm(null); setDeleteReason('');
              }}>
                Yes, Delete Project
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      <ConfirmModal isOpen={!!confirmAction} onClose={() => setConfirmAction(null)}
        title={confirmAction?.title || 'Confirm'} message={confirmAction?.message}
        confirmLabel="Delete" confirmColor="red"
        onConfirm={() => { if (confirmAction?.onConfirm) confirmAction.onConfirm(); setConfirmAction(null); }} />
      <TemplatePickerModal isOpen={templatePickerDisclosure.isOpen} onClose={templatePickerDisclosure.onClose}
        rowGroupId={templatePickerGroup} onCreated={() => { if (onRefresh) onRefresh(); }} />

      {/* ── Invoice Totals / Filter Modal ── */}
      {showTotalsModal && (
        <Modal isOpen={true} onClose={() => setShowTotalsModal(false)} size="5xl" isCentered scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent maxH="88vh" display="flex" flexDirection="column">
            <ModalHeader pb={2} flexShrink={0}>
              <Flex align="center" gap={3}>
                <Box bg="green.500" color="white" px={2} py={0.5} borderRadius="md" fontSize="12px" fontWeight="800">£ IN</Box>
                <Box bg="red.500" color="white" px={2} py={0.5} borderRadius="md" fontSize="12px" fontWeight="800">£ OUT</Box>
                <Text fontSize="14px" fontWeight="700">Invoice Totals & Status Filters</Text>
              </Flex>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={3} overflowY="auto" flex={1}>
              <Text fontSize="11px" color="gray.500" mb={3}>
                Select which invoice statuses and currencies to include in the totals. Leave statuses unchecked to show all.
              </Text>

              {/* ── 3-column layout: Options | £IN | £OUT ── */}
              <Flex gap={4} align="flex-start">

                {/* LEFT: Options + Currency + Date */}
                <Box w="260px" flexShrink={0}>

                  {/* Row options */}
                  <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mb={2}>Display Options</Text>
                  {[
                    { state: hideNoInvoice,         set: setHideNoInvoice,         label: 'Hide rows without financials',        sub: 'Only rows with at least one invoice' },
                    { state: hideNoFinancialGroups,  set: setHideNoFinancialGroups, label: 'Hide groups without financials',       sub: 'Only groups containing invoices' },
                    { state: countOnlyPrimary,       set: setCountOnlyPrimary,      label: 'Count once (primary group only)',      sub: 'Prevents double-counting across groups' },
                  ].map(({ state, set, label, sub }) => (
                    <Flex key={label} align="center" gap={2} mb={2} p={2} bg="gray.50" borderRadius="md"
                      border="1px solid" borderColor="gray.200" cursor="pointer" onClick={() => set(p => !p)}>
                      <Box w="16px" h="16px" flexShrink={0} border="2px solid"
                        borderColor={state ? 'blue.500' : 'gray.300'} bg={state ? 'blue.500' : 'white'}
                        borderRadius="3px" display="flex" alignItems="center" justifyContent="center">
                        {state && <Box as="span" fontSize="10px" color="white" fontWeight="800">✓</Box>}
                      </Box>
                      <Box>
                        <Text fontSize="11px" fontWeight="600" color="gray.700">{label}</Text>
                        <Text fontSize="9px" color="gray.400">{sub}</Text>
                      </Box>
                    </Flex>
                  ))}

                  {/* Currency filter */}
                  <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mt={3} mb={2}>Currency Filter</Text>
                  <Text fontSize="9px" color="gray.400" mb={2}>Select which currencies count toward cash IN/OUT totals. Default: GBP only.</Text>
                  {[
                    { value: 'gbp', label: 'GBP (£)', color: 'green' },
                    { value: 'all', label: 'All currencies', color: 'blue' },
                  ].map(({ value, label, color }) => (
                    <Flex key={value} align="center" gap={2} mb={1.5} p={2} bg="gray.50" borderRadius="md"
                      border="1px solid" borderColor={invCurrencies.includes(value) ? `${color}.300` : 'gray.200'}
                      cursor="pointer"
                      onClick={() => setInvCurrencies(invCurrencies.includes(value) ? invCurrencies.filter(c => c !== value) : [...invCurrencies, value])}>
                      <Box w="16px" h="16px" flexShrink={0} border="2px solid"
                        borderColor={invCurrencies.includes(value) ? `${color}.500` : 'gray.300'}
                        bg={invCurrencies.includes(value) ? `${color}.500` : 'white'}
                        borderRadius="3px" display="flex" alignItems="center" justifyContent="center">
                        {invCurrencies.includes(value) && <Box as="span" fontSize="10px" color="white" fontWeight="800">✓</Box>}
                      </Box>
                      <Text fontSize="11px" fontWeight="600" color="gray.700">{label}</Text>
                    </Flex>
                  ))}

                  {/* BBX/Barter VAT option */}
                  <Box mt={3} p={2} bg="orange.50" borderRadius="md" border="1px solid" borderColor="orange.200">
                    <Text fontSize="10px" fontWeight="800" color="orange.700" textTransform="uppercase" letterSpacing="0.06em" mb={1}>Barter / BBX</Text>
                    <Flex align="flex-start" gap={2} cursor="pointer" onClick={() => setInvIncludeBarterVat(p => !p)}>
                      <Box w="16px" h="16px" mt="1px" flexShrink={0} border="2px solid"
                        borderColor={invIncludeBarterVat ? 'orange.500' : 'gray.300'}
                        bg={invIncludeBarterVat ? 'orange.500' : 'white'}
                        borderRadius="3px" display="flex" alignItems="center" justifyContent="center">
                        {invIncludeBarterVat && <Box as="span" fontSize="10px" color="white" fontWeight="800">✓</Box>}
                      </Box>
                      <Box>
                        <Text fontSize="11px" fontWeight="600" color="orange.800">Include BBX VAT in VAT & Balance</Text>
                        <Text fontSize="9px" color="orange.600">Counts BBX VAT cash liability/claim in the VAT and BAL columns only — not in cash IN/OUT totals</Text>
                      </Box>
                    </Flex>
                  </Box>

                  {/* Date range filter */}
                  <Text fontSize="10px" fontWeight="800" color="gray.500" textTransform="uppercase" letterSpacing="0.06em" mt={3} mb={2}>Date Range Filter</Text>
                  <HStack spacing={2} mb={2}>
                    {['issue','due'].map(f => (
                      <Box key={f} as="button" px={2} py={0.5} borderRadius="md" fontSize="11px" fontWeight="600" cursor="pointer"
                        bg={invDateField === f ? 'blue.500' : 'gray.200'} color={invDateField === f ? 'white' : 'gray.700'}
                        onClick={() => setInvDateField(f)}>
                        {f === 'issue' ? 'Issued Date' : 'Due Date'}
                      </Box>
                    ))}
                  </HStack>
                  <VStack spacing={1} align="stretch">
                    <Box>
                      <Text fontSize="9px" color="gray.500" mb={0.5}>From</Text>
                      <Input size="xs" type="date" value={invDateFrom} onChange={e => setInvDateFrom(e.target.value)} />
                    </Box>
                    <Box>
                      <Text fontSize="9px" color="gray.500" mb={0.5}>To</Text>
                      <Input size="xs" type="date" value={invDateTo} onChange={e => setInvDateTo(e.target.value)} />
                    </Box>
                    {(invDateFrom || invDateTo) && (
                      <Button size="xs" variant="ghost" colorScheme="red"
                        onClick={() => { setInvDateFrom(''); setInvDateTo(''); }}>
                        Clear Dates
                      </Button>
                    )}
                  </VStack>
                </Box>

                {/* Vertical divider */}
                <Box w="1px" bg="gray.200" alignSelf="stretch" flexShrink={0} />

                {/* MIDDLE: £ IN */}
                <Box flex={1} minW={0}>
                  <Flex align="center" gap={2} mb={2}>
                    <Box bg="green.500" color="white" px={2} py={0.5} borderRadius="md" fontSize="11px" fontWeight="800">£ IN</Box>
                    <Text fontSize="12px" fontWeight="700" color="green.700">Revenue / Sales Invoices</Text>
                  </Flex>
                  <Flex align="center" gap={2} p={1.5} mb={1} borderRadius="md" cursor="pointer"
                    bg={filterInStatuses.length === IN_FILTER_OPTS.length ? 'green.100' : 'gray.50'}
                    border="1px solid" borderColor="green.200"
                    onClick={() => filterInStatuses.length === IN_FILTER_OPTS.length
                      ? setFilterInStatuses([]) : setFilterInStatuses(IN_FILTER_OPTS.map(o => o.value))}>
                    <Box w="14px" h="14px" flexShrink={0} border="2px solid"
                      borderColor={filterInStatuses.length === IN_FILTER_OPTS.length ? 'green.500' : filterInStatuses.length > 0 ? 'green.400' : 'gray.400'}
                      bg={filterInStatuses.length === IN_FILTER_OPTS.length ? 'green.500' : 'white'}
                      borderRadius="3px" display="flex" alignItems="center" justifyContent="center">
                      {filterInStatuses.length === IN_FILTER_OPTS.length && <Box w="6px" h="6px" bg="white" borderRadius="1px" />}
                      {filterInStatuses.length > 0 && filterInStatuses.length < IN_FILTER_OPTS.length && <Box w="8px" h="2px" bg="green.500" />}
                    </Box>
                    <Text fontSize="12px" fontWeight="700" color="green.700">
                      {filterInStatuses.length === IN_FILTER_OPTS.length ? 'Deselect All' : 'Select All'}
                    </Text>
                  </Flex>
                  <Box overflowY="auto" maxH="380px" pr={1}
                    css={{ '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '2px' } }}>
                    <VStack spacing={1} align="stretch">
                      {IN_FILTER_OPTS.map(opt => (
                        <Flex key={opt.value} align="center" gap={2} p={1.5} borderRadius="md" cursor="pointer"
                          bg={filterInStatuses.includes(opt.value) ? 'green.50' : 'transparent'}
                          border="1px solid" borderColor={filterInStatuses.includes(opt.value) ? 'green.300' : 'transparent'}
                          _hover={{ bg: 'green.50', borderColor: 'green.200' }}
                          onClick={() => toggleStatus(filterInStatuses, setFilterInStatuses, opt.value)}>
                          <Box w="14px" h="14px" flexShrink={0} border="2px solid"
                            borderColor={filterInStatuses.includes(opt.value) ? 'green.500' : 'gray.300'}
                            bg={filterInStatuses.includes(opt.value) ? 'green.500' : 'white'}
                            borderRadius="3px" display="flex" alignItems="center" justifyContent="center">
                            {filterInStatuses.includes(opt.value) && <Box w="6px" h="6px" bg="white" borderRadius="1px" />}
                          </Box>
                          <Text fontSize="12px" color={opt.value.startsWith('__') ? 'orange.600' : 'gray.700'}
                            fontWeight={opt.value.startsWith('__') ? '600' : '400'}>
                            {opt.label}
                          </Text>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                  {filterInStatuses.length > 0 && (
                    <Button size="xs" variant="ghost" colorScheme="gray" mt={2}
                      onClick={() => setFilterInStatuses([])}>Clear £ IN</Button>
                  )}
                </Box>

                {/* Vertical divider */}
                <Box w="1px" bg="gray.200" alignSelf="stretch" flexShrink={0} />

                {/* RIGHT: £ OUT */}
                <Box flex={1} minW={0}>
                  <Flex align="center" gap={2} mb={2}>
                    <Box bg="red.500" color="white" px={2} py={0.5} borderRadius="md" fontSize="11px" fontWeight="800">£ OUT</Box>
                    <Text fontSize="12px" fontWeight="700" color="red.700">Supplier / Purchase Invoices</Text>
                  </Flex>
                  <Flex align="center" gap={2} p={1.5} mb={1} borderRadius="md" cursor="pointer"
                    bg={filterOutStatuses.length === OUT_FILTER_OPTS.length ? 'red.100' : 'gray.50'}
                    border="1px solid" borderColor="red.200"
                    onClick={() => filterOutStatuses.length === OUT_FILTER_OPTS.length
                      ? setFilterOutStatuses([]) : setFilterOutStatuses(OUT_FILTER_OPTS.map(o => o.value))}>
                    <Box w="14px" h="14px" flexShrink={0} border="2px solid"
                      borderColor={filterOutStatuses.length === OUT_FILTER_OPTS.length ? 'red.500' : filterOutStatuses.length > 0 ? 'red.400' : 'gray.400'}
                      bg={filterOutStatuses.length === OUT_FILTER_OPTS.length ? 'red.500' : 'white'}
                      borderRadius="3px" display="flex" alignItems="center" justifyContent="center">
                      {filterOutStatuses.length === OUT_FILTER_OPTS.length && <Box w="6px" h="6px" bg="white" borderRadius="1px" />}
                      {filterOutStatuses.length > 0 && filterOutStatuses.length < OUT_FILTER_OPTS.length && <Box w="8px" h="2px" bg="red.500" />}
                    </Box>
                    <Text fontSize="12px" fontWeight="700" color="red.700">
                      {filterOutStatuses.length === OUT_FILTER_OPTS.length ? 'Deselect All' : 'Select All'}
                    </Text>
                  </Flex>
                  <Box overflowY="auto" maxH="380px" pr={1}
                    css={{ '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '2px' } }}>
                    <VStack spacing={1} align="stretch">
                      {OUT_FILTER_OPTS.map(opt => (
                        <Flex key={opt.value} align="center" gap={2} p={1.5} borderRadius="md" cursor="pointer"
                          bg={filterOutStatuses.includes(opt.value) ? 'red.50' : 'transparent'}
                          border="1px solid" borderColor={filterOutStatuses.includes(opt.value) ? 'red.300' : 'transparent'}
                          _hover={{ bg: 'red.50', borderColor: 'red.200' }}
                          onClick={() => toggleStatus(filterOutStatuses, setFilterOutStatuses, opt.value)}>
                          <Box w="14px" h="14px" flexShrink={0} border="2px solid"
                            borderColor={filterOutStatuses.includes(opt.value) ? 'red.500' : 'gray.300'}
                            bg={filterOutStatuses.includes(opt.value) ? 'red.500' : 'white'}
                            borderRadius="3px" display="flex" alignItems="center" justifyContent="center">
                            {filterOutStatuses.includes(opt.value) && <Box w="6px" h="6px" bg="white" borderRadius="1px" />}
                          </Box>
                          <Text fontSize="12px" color={opt.value.startsWith('__') ? 'orange.600' : 'gray.700'}
                            fontWeight={opt.value.startsWith('__') ? '600' : '400'}>
                            {opt.label}
                          </Text>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                  {filterOutStatuses.length > 0 && (
                    <Button size="xs" variant="ghost" colorScheme="gray" mt={2}
                      onClick={() => setFilterOutStatuses([])}>Clear £ OUT</Button>
                  )}
                </Box>
              </Flex>
            </ModalBody>

            {/* ── Footer ── */}
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={2} flexShrink={0} flexWrap="wrap">
              {(filterInStatuses.length > 0 || filterOutStatuses.length > 0) && (
                <Button size="sm" variant="ghost" colorScheme="red"
                  onClick={() => { setFilterInStatuses([]); setFilterOutStatuses([]); }}>
                  Clear All Status Filters
                </Button>
              )}
              {onOpenBookkeeper && (filterInStatuses.length > 0 || filterOutStatuses.length > 0) && (
                <Button size="sm" variant="outline" colorScheme="blue"
                  onClick={() => { setShowTotalsModal(false); onOpenBookkeeper(
                    filterInStatuses.length > 0 && filterOutStatuses.length === 0 ? 'out' :
                    filterOutStatuses.length > 0 && filterInStatuses.length === 0 ? 'in' : ''
                  ); }}>
                  Open in Bookkeeper
                </Button>
              )}
              <Button size="sm" colorScheme="blue" onClick={() => setShowTotalsModal(false)}>
                Apply & Close
              </Button>
              <Button size="sm" variant="ghost" colorScheme="gray"
                onClick={() => { setShowTotals(false); setFilterInStatuses([]); setFilterOutStatuses([]); setHideNoInvoice(true); setHideNoFinancialGroups(true); setCountOnlyPrimary(true); setInvDateFrom(''); setInvDateTo(''); setShowTotalsModal(false); }}>
                Hide Totals
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
      <CompanyQuickViewModal
        companyId={quickViewCompany}
        isOpen={!!quickViewCompany}
        onClose={() => setQuickViewCompany(null)}
        onEdit={(company) => setEditCompanyId(company.id)}
      />
      {editCompanyId && (
        <CompaniesListModal
          isOpen={!!editCompanyId}
          onClose={() => setEditCompanyId(null)}
          initialCompanyId={editCompanyId}
          initialEdit
        />
      )}
      <ContactQuickViewModal
        contactId={quickViewContact}
        isOpen={!!quickViewContact}
        onClose={() => setQuickViewContact(null)}
        onEdit={(contact) => setEditContactId(contact.id)}
      />
      {/* Full-edit modal launched from the quick view's pencil icon */}
      {editContactId && (
        <ContactsListModal
          isOpen={!!editContactId}
          onClose={() => setEditContactId(null)}
          initialContactId={editContactId}
          initialEdit
        />
      )}
    </Box>
  );
}
